import React, { Suspense } from "react";

import {
    ColumnBuilder,
    ColumnOptions,
    FormattedView,
} from "@impero-com/common/components/dataTable";
import { DeferredContent } from "@impero-com/common/components/deferredContent";
import { AsyncFilter } from "@impero-com/common/components/filter/asyncFilter";
import { NoAccessCard } from "@impero-com/common/components/noAccessCard/index";
import { WithRouterProps } from "@impero-com/common/components/routerUtils";
import { LoggedInHTTPError } from "@impero-com/common/errorPages/loggedInHTTPError";
import IconExport from "@impero-com/common/img/export.svg";
import * as Cmp from "@impero-com/common/utils/comparison";
import * as DV from "@impero-com/common/utils/dataView";
import { Interval, intervalsOverlap } from "@impero-com/common/utils/date";
import { ExportableArray } from "@impero-com/common/utils/export";
import { formatFileNameForExport } from "@impero-com/common/utils/formatting";
import { t } from "@impero-com/common/utils/i18n";
import {
    addFieldFilter,
    AsyncDataViewIndex,
    MATCH_TYPE_EXACT_IGNORE_CASE,
    QueryResult,
    removeFieldFilter,
    SimpleAndFilter,
    SimpleFieldFilter,
    SimpleOrFilter,
} from "@impero-com/common/utils/indexing";
import { assertNever } from "@impero-com/common/utils/obj";
import * as XLSXUtils from "@impero-com/common/utils/xlsx";
import {
    ActionSpec,
    ContentWithoutBorder,
    emitNotification,
    InfoTooltip,
    PageSpinner,
    renderMarkdown,
    TabSpec,
} from "@impero-com/ui";
import { DatePicker as AntdDatePicker } from "antd";
import { AxiosError } from "axios";
import { cx } from "class-variance-authority";
import Color from "color";
import { keyBy } from "lodash-es";
import memoizeOne from "memoize-one";
import moment, { Moment } from "moment-timezone";
import { useLocation, useNavigate } from "react-router-dom";
import { default as saveAs } from "save-as";

import {
    CellCoord,
    BinaryAssessmentStatus,
    BinaryNodeScope,
    SetRiskAssessment,
    AssessmentStatus,
    MitigatingControl,
    MissingRequiredColumnsDetails,
    RiskDirectoryNodeWithDetailsDto as RiskDirectoryNodeDto,
    RiskDirectoryResourceDto as RiskDirectoryDto,
    ResourceValidityInterval,
    ControlOverview,
    ControlProgramOverview,
    EntityOverviewListDto,
    SavedBlob,
    UpdatedRiskDirectoryNode,
    ControlId,
    ControlProgramId,
} from "api/generated/model";
import { patchRisksRiskDirectoryIdNodes } from "api/generated/risks/risks";
import { DatePicker } from "components/datePicker";
import { Mode, ModeType, SwitchableTreeView } from "components/switchable";
import IconRiskDir from "img/risk-directory.svg";
import shareIcon from "img/share-icon.svg";
import {
    ConditionalPageStateManager,
    RenderContentArgs,
} from "org/pageState/components/pageStateManager";
import {
    EntityId,
    MetadataColumnId,
    RiskNodeId,
} from "org/pageState/convert/risks/common";
import {
    pageStateRiskMapsCodecs,
    RiskMapPageStateRaw,
    createDecodeRiskMapPageStateFn,
} from "org/pageState/convert/risks/riskMaps";
import { DEFAULT_RISK_MAP_PAGE_STATE } from "org/pageState/defaults";
import { IdAndName } from "org/pageState/models";
import { PAGE_KEY } from "org/pageState/pageKey";
import { PageFilterAndColumns } from "org/pageState/types";
import { splitPageStateWithCallback } from "org/pageState/utils";
import * as R from "org/risks/types";
import { ProcessNode, RISK_EXTRACTOR_KEYS } from "org/risks/types";
import { RISK_MODULE } from "org/risks/url";
import { AssessmentStatusColumn } from "org/risks/widgets/assessmentStatusColumn";
import { UpdateRootNodeLinkedResourcesCallback } from "org/risks/widgets/attachedFilesField";
import { NameColumn } from "org/risks/widgets/nameColumn";
import { downgradeExtractorType } from "utils/dataView";
import { STATUS_CODE_ERROR_OPERATION_FAILED } from "utils/errorCodes";
import { newExportMetadata } from "utils/export";
import { Guid } from "utils/guid";
import { useFeatureFlag } from "utils/hooks/useFeatureFlag";

import {
    RISK_DIRECTORY_DATE_SEARCH_PARAM,
    RISK_DIRECTORY_FROM_DATE_SEARCH_PARAM,
    RISK_DIRECTORY_TO_DATE_SEARCH_PARAM,
    RISK_MAP_HISTORY_HASH,
    RISK_MAP_INSIGHTS_HASH_OLD,
} from "..";
import { OrgApplicationBar } from "../../../../components/applicationBar/orgApplicationBar";
import { FormulaComputer } from "../formulaComputer";
import { EnumMap } from "../impactLikelihood";
import { MapNodeDrawer } from "../mapDrawer";
import { useRiskDirectoryViewQuery } from "../queries/useRiskDirectoryViewQuery";
import { useRiskEntitiesQuery } from "../queries/useRiskEntitiesQuery";
import { useRiskNodesQuery } from "../queries/useRiskNodesQuery";
import * as T from "../types";
import { CommonGetters } from "../types";
import * as U from "../utils";

import { RiskHeatMap } from "./components/RiskHeatmap";
import {
    INHERENT_MODE,
    MapMode,
    RESIDUAL_MODE,
} from "./components/RiskHeatmapTypes";
import { RiskInsightsTable } from "./components/RiskInsightsTable";
import { RiskMapAISummary } from "./components/RiskMapAISummary";
import { RiskMatrixExportOptions, exportRiskMatrix } from "./export";
import { ExportRiskMatrixPopup } from "./exportRiskMatrix";
import { FailedAssessmentModal } from "./failedAssessment";
import { mergeRootNodes } from "./merge";
import * as theme from "./theme.css";

export const PATH_SEPARATOR = "\xA0/\xA0";
const HISTORY_FEATURE_START = moment(
    T.RISK_MAP_HISTORY_FEATURE_AVAILABILITY
).startOf("day");

export type DateChange =
    | { type: "FROM_EXACT_TIMESTAMP"; date: Moment }
    | { type: "FROM_DATE_PICKER"; date: Moment }
    | { type: "UNSET" };
export interface SwitchableViewArgs {
    switchableViewMode: ModeType | undefined;
    onSwitchableViewModeChange: ((mode: ModeType) => void) | undefined;
    visibleColumnIdsForTableView: Set<string> | undefined;
    onVisibleColumnsForTableViewChanged:
        | ((visibleColumnIds: Set<string>) => void)
        | undefined;
    visibleColumnIdsForTreeView: Set<string> | undefined;
    onVisibleColumnsForTreeViewChanged:
        | ((visibleColumnIds: Set<string>) => void)
        | undefined;
}

function _computeArgsForSwitchableTreeView<
    ST extends PageFilterAndColumns & { showTableView: boolean },
>(
    pageState: ST | undefined,
    onPageStateChange: ((newPageState: ST) => void) | undefined,
    visibleColumnIds: Set<string> | undefined,
    onVisibleColumnsChange:
        | ((visibleColumnIds: Set<string>) => void)
        | undefined
): SwitchableViewArgs {
    const switchableViewMode = pageState?.showTableView
        ? Mode.TABLE
        : Mode.TREE;
    const onSwitchableViewModeChange =
        pageState &&
        onPageStateChange &&
        ((mode: ModeType) => {
            onPageStateChange({
                ...pageState,
                showTableView: mode === Mode.TABLE,
            });
        });

    const visibleColumnIdsForTableView = visibleColumnIds;

    const visibleColumnIdsForTreeView = new Set(visibleColumnIds);
    visibleColumnIdsForTreeView.delete(RISK_EXTRACTOR_KEYS.PATH);

    // This function is used to create an onVisibleColumnsChange handler for tree and table view.
    // Changes between view-specific previousColumns and newColumns are applied to the unified totalColumnIds
    // which gets sent upwards through the original onVisibleColumnsChange handler
    const viewSpecificVisibleColumnsHandler =
        onVisibleColumnsChange &&
        ((previousColumns: Set<string>) => (newColumns: Set<string>) => {
            const totalColumnIds = new Set(visibleColumnIds);

            // Add columns to totalColumnIds that were added
            newColumns.forEach((column) => {
                if (!previousColumns.has(column)) {
                    totalColumnIds.add(column);
                }
            });

            // Remove columns from totalColumnIds that were removed
            previousColumns.forEach((column) => {
                if (!newColumns.has(column)) {
                    totalColumnIds.delete(column);
                }
            });

            onVisibleColumnsChange(totalColumnIds);
        });

    const onVisibleColumnsForTableViewChanged =
        viewSpecificVisibleColumnsHandler &&
        visibleColumnIdsForTableView &&
        viewSpecificVisibleColumnsHandler(visibleColumnIdsForTableView);

    const onVisibleColumnsForTreeViewChanged =
        viewSpecificVisibleColumnsHandler &&
        visibleColumnIdsForTreeView &&
        viewSpecificVisibleColumnsHandler(visibleColumnIdsForTreeView);

    return {
        switchableViewMode,
        onSwitchableViewModeChange,
        visibleColumnIdsForTableView,
        onVisibleColumnsForTableViewChanged,
        visibleColumnIdsForTreeView,
        onVisibleColumnsForTreeViewChanged,
    };
}
export const computeArgsForSwitchableTreeView = memoizeOne(
    _computeArgsForSwitchableTreeView
);

export function MapPageFnWrapper(props: MapPagePropsWithoutRiskInsights) {
    const riskInsightsEnabled = useFeatureFlag("riskInsights");
    return <MapPage {...props} riskInsightsEnabled={riskInsightsEnabled} />;
}

type InsightsWrapperProps = Omit<MapPagePropsBase, "riskInsightsEnabled"> & {
    mode: {
        type: "InsightsMap";
        fromDate: Moment | null;
        toDate: Moment | null;
    };
};
export function MapPageInsightsWrapper(props: InsightsWrapperProps) {
    // The suspense needs to be moved down the tree, so that only the risk map itself is suspended
    return (
        <Suspense fallback={null}>
            <MapPageInsightsWrapperContent {...props} />
        </Suspense>
    );
}
function MapPageInsightsWrapperContent(props: InsightsWrapperProps) {
    const navigate = useNavigate();
    const location = useLocation();
    const riskInsightsEnabled = useFeatureFlag("riskInsights");
    const searchParams = new URLSearchParams(location.search);
    const fromDate =
        searchParams.get(RISK_DIRECTORY_FROM_DATE_SEARCH_PARAM) ?? undefined;
    const toDate =
        searchParams.get(RISK_DIRECTORY_TO_DATE_SEARCH_PARAM) ?? undefined;
    const { data: riskDirData } = useRiskDirectoryViewQuery(
        props.riskDirectoryView.content.resource.id.toString(),
        toDate
    );
    const { data: riskEntitiesData } = useRiskEntitiesQuery(toDate);
    const { data: riskNodesData } = useRiskNodesQuery(
        props.riskDirectoryView.content.resource.id.toString(),
        toDate
    );
    const { data: fromRiskNodesData } = useRiskNodesQuery(
        props.riskDirectoryView.content.resource.id.toString(),
        fromDate
    );
    const { data: fromRiskDirData } = useRiskDirectoryViewQuery(
        props.riskDirectoryView.content.resource.id.toString(),
        fromDate
    );
    const handleSetToDate = (date: Moment | null) => {
        if (!date) {
            return;
        }
        const newSearchParams = new URLSearchParams(document.location.search);
        newSearchParams.set(
            RISK_DIRECTORY_TO_DATE_SEARCH_PARAM,
            date.endOf("day").toISOString()
        );
        navigate(
            {
                pathname: location.pathname,
                hash: location.hash,
                search: newSearchParams.toString(),
            },
            {
                replace: true,
            }
        );
    };

    const fromData = {
        riskDirData: fromRiskDirData,
        riskEntitiesData: props.entityList,
        riskNodesData: fromRiskNodesData,
    };

    // Create updated common getters that use the new rootNode data
    const fromCommon = React.useMemo((): CommonGetters => {
        if (!fromRiskNodesData) {
            return props.common;
        }

        const fromRootNode = fromRiskNodesData.content.resource.rootNode;

        // Get computed node props for the merged rootNode
        const computedNodeProps = props.common.computedNodeProps(fromRootNode);

        // Override the methods that depend on rootNode
        return {
            ...props.common,
            getComputedNodeEntityIds: (nodeId: Guid): number[] => {
                const nodeProps:
                    | { parentId?: Guid; entityIds: number[] }
                    | undefined = computedNodeProps[nodeId];
                if (!nodeProps) {
                    console.warn(
                        `Node ${nodeId} not found in computedNodeProps for historical data`
                    );
                    return [];
                }
                return nodeProps.entityIds;
            },
            getComputedEntityIds: (nodeId: Guid): number[] => {
                const entities: number[] = [];
                let nextId: Guid | undefined = nodeId;
                while (nextId) {
                    const nodeProps:
                        | { parentId?: Guid; entityIds: number[] }
                        | undefined = computedNodeProps[nextId];
                    if (!nodeProps) {
                        console.warn(
                            `Node ${nextId} not found in computedNodeProps for historical data`
                        );
                        break;
                    }
                    entities.push(...nodeProps.entityIds);
                    nextId = nodeProps.parentId;
                }
                return entities;
            },
        };
    }, [props.common, fromRiskNodesData]);
    const mergedCommon = React.useMemo((): CommonGetters => {
        if (!fromRiskNodesData) {
            return props.common;
        }

        const mergedRootNode = mergeRootNodes(
            riskNodesData.content.resource.rootNode,
            fromRiskNodesData.content.resource.rootNode
        );

        // Get computed node props for the merged rootNode
        const computedNodeProps =
            props.common.computedNodeProps(mergedRootNode);

        // Override the methods that depend on rootNode
        return {
            ...props.common,
            getComputedNodeEntityIds: (nodeId: Guid): number[] => {
                const nodeProps:
                    | { parentId?: Guid; entityIds: number[] }
                    | undefined = computedNodeProps[nodeId];
                if (!nodeProps) {
                    console.warn(
                        `Node ${nodeId} not found in computedNodeProps for merged data`
                    );
                    return [];
                }
                return nodeProps.entityIds;
            },
            getComputedEntityIds: (nodeId: Guid): number[] => {
                const entities: number[] = [];
                let nextId: Guid | undefined = nodeId;
                while (nextId) {
                    const nodeProps:
                        | { parentId?: Guid; entityIds: number[] }
                        | undefined = computedNodeProps[nextId];
                    if (!nodeProps) {
                        console.warn(
                            `Node ${nextId} not found in computedNodeProps for merged data`
                        );
                        break;
                    }
                    entities.push(...nodeProps.entityIds);
                    nextId = nodeProps.parentId;
                }
                return entities;
            },
        };
    }, [props.common, riskNodesData, fromRiskNodesData]);

    return (
        <MapPage
            {...props}
            // Without the key, updating to date sometimes does not trigger an update
            //
            // This needs to be reworked in the future once the component is recomposed
            // and the reload behavior when selecting the page is fixed
            key={toDate}
            riskDirectoryView={riskDirData}
            entityList={riskEntitiesData}
            rootNode={riskNodesData}
            mode={{
                ...props.mode,
                diffData: fromData,
                diffCommon: fromCommon,
                mergedCommon,
                setToDate: handleSetToDate,
                currentToDate: toDate ? moment(toDate) : undefined,
            }}
            riskInsightsEnabled={riskInsightsEnabled}
            navigate={navigate}
            location={location}
        />
    );
}

type CacheKey = "map" | "insightStart" | "insightMerge";

export class MapPage extends React.PureComponent<MapPageProps, MapPageState> {
    constructor(props: MapPageProps) {
        super(props);
        this.state = {
            drawer: undefined,
            status: { type: "DEFAULT" },
            popupState: undefined,
            // In historical data tab, component state is used instead of PageStateManager
            pageState: createDecodeRiskMapPageStateFn([])(
                DEFAULT_RISK_MAP_PAGE_STATE
            ),
            aiSummaryEnabled: false,
            insightsEditorDates: null,
        };
    }

    nodeTypeToNodeExtractors = memoizeMap(
        (
            _key: CacheKey,
            riskDirectory: R.RiskDirectoryView,
            savedBlobs: SavedBlob[]
        ) => this.buildNodeTypeToNodeExtractors(riskDirectory, savedBlobs)
    );
    mapDataView = memoizeMap(
        (
            key: CacheKey,
            riskDirectory: R.RiskDirectoryView,
            rootNode: RiskDirectoryNodeDto,
            entityList: EntityOverviewListDto,
            common: T.CommonGetters = this.props.common
        ) =>
            this.buildMapDataView(
                key,
                riskDirectory,
                rootNode,
                entityList,
                common
            )
    );
    mapQueryResult = memoizeMap(
        (
            key: CacheKey,
            riskDirectory: R.RiskDirectoryView,
            rootNode: RiskDirectoryNodeDto,
            filter: SimpleAndFilter | null,
            entityList: EntityOverviewListDto,
            common: T.CommonGetters = this.props.common
        ) =>
            this.buildMapQueryResult(
                key,
                riskDirectory,
                rootNode,
                filter,
                entityList,
                common
            )
    );
    mapDataViewIndex = memoizeMap(
        (
            key: CacheKey,
            riskDirectory: R.RiskDirectoryView,
            entityList: EntityOverviewListDto,
            common: T.CommonGetters = this.props.common
        ) => {
            let oldRootNode: T.RiskDirectoryNodeDtoWithChanges | undefined;
            let mapDataViewIndex:
                | AsyncDataViewIndex<DV.NodeDescriptor<T.MapNode>>
                | undefined;
            return (rootNode: T.RiskDirectoryNodeDtoWithChanges) => {
                if (
                    oldRootNode === undefined ||
                    mapDataViewIndex === undefined
                ) {
                    oldRootNode = rootNode;
                    mapDataViewIndex = this.buildMapDataViewIndex(
                        key,
                        riskDirectory,
                        rootNode,
                        entityList,
                        common
                    );
                } else if (rootNode !== oldRootNode) {
                    oldRootNode = rootNode;
                    const { expanders } = this.buildMapExtractorExpanders(
                        key,
                        riskDirectory
                    );
                    mapDataViewIndex = new AsyncDataViewIndex<
                        DV.NodeDescriptor<T.MapNode>
                    >(
                        mapDataViewIndex,
                        this.mapDataView(
                            key,
                            riskDirectory,
                            rootNode,
                            entityList,
                            common
                        ),
                        {
                            // There is no node creation / deletion on the risk map view
                            updated: rootNode.lastChanges?.updated?.flatMap(
                                (node) =>
                                    this.expandNode(node, common).map(
                                        (expanded) => expanded.id
                                    )
                            ),
                        },
                        expanders
                    );
                }
                return mapDataViewIndex;
            };
        }
    );
    mapColumnBuilders = memoizeMap(
        (_key: CacheKey, riskDirectory: R.RiskDirectoryView) =>
            this.buildMapColumnBuilders(riskDirectory)
    );
    controlMap = memoizeMap(computeControlMap);
    controlProgramMap = memoizeMap(computeControlProgramMap);
    mapDrawerNode = memoizeMap(
        (
            key: CacheKey,
            riskDirectory: R.RiskDirectoryView,
            rootNode: RiskDirectoryNodeDto,
            entityList: EntityOverviewListDto,
            nodeId: string,
            common: T.CommonGetters = this.props.common
        ) => {
            const dataView = this.mapDataView(
                key,
                riskDirectory,
                rootNode,
                entityList,
                common
            );
            return DV.find(
                dataView.dataSource.iter(),
                (desc) => dataView.dataSource.getId(desc) === nodeId
            );
        }
    );
    focusDataViewIndex = memoizeMap(
        (
            key: CacheKey,
            riskDirectory: R.RiskDirectoryView,
            filter: SimpleAndFilter | null,
            entityList: EntityOverviewListDto,
            common: T.CommonGetters = this.props.common
        ) => {
            let oldRootNode: T.RiskDirectoryNodeDtoWithChanges | undefined;
            let focusMapDataViewIndex:
                | AsyncDataViewIndex<DV.NodeDescriptor<T.MapNode>>
                | undefined;
            return (rootNode: T.RiskDirectoryNodeDtoWithChanges) => {
                if (
                    oldRootNode === undefined ||
                    focusMapDataViewIndex === undefined
                ) {
                    oldRootNode = rootNode;
                    focusMapDataViewIndex = new AsyncDataViewIndex(
                        this.mapQueryResult(
                            key,
                            riskDirectory,
                            rootNode,
                            filter,
                            entityList,
                            common
                        ).then((queryResult) => queryResult.dataView),
                        ["inherentFocus", "residualFocus"]
                    );
                } else if (rootNode !== oldRootNode) {
                    oldRootNode = rootNode;
                    focusMapDataViewIndex = new AsyncDataViewIndex<
                        DV.NodeDescriptor<T.MapNode>
                    >(
                        focusMapDataViewIndex,
                        this.mapQueryResult(
                            key,
                            riskDirectory,
                            rootNode,
                            filter,
                            entityList,
                            common
                        ).then((queryResult) => queryResult.dataView),
                        {
                            // There is no node creation / deletion on the risk map view
                            updated: rootNode.lastChanges?.updated?.flatMap(
                                (node) =>
                                    this.expandNode(node, common).map(
                                        (expanded) => expanded.id
                                    )
                            ),
                        }
                    );
                }
                return focusMapDataViewIndex;
            };
        }
    );
    focusQueryResult = memoizeMap(
        (
            key: CacheKey,
            riskDirectory: R.RiskDirectoryView,
            rootNode: RiskDirectoryNodeDto,
            filter: SimpleAndFilter | null,
            coords: CellCoord[] | null,
            entityList: EntityOverviewListDto,
            mapMode: MapMode,
            common: T.CommonGetters = this.props.common
        ) =>
            this.buildFocusQueryResult(
                key,
                riskDirectory,
                rootNode,
                filter,
                coords,
                entityList,
                mapMode,
                common
            )
    );
    focusFilter = memoizeMap(
        (
            _key: CacheKey,
            riskDirectory: R.RiskDirectoryView,
            mapMode: MapMode,
            coords: CellCoord[] | null
        ) =>
            coords !== null
                ? this.buildFocusFilter(riskDirectory, mapMode, coords)
                : null
    );

    canEditNode = (
        node: R.ProcessNode | T.ExpandedRiskNode | T.MapNode,
        historyMode: boolean
    ): boolean => {
        const { riskDirectoryView, rootNode } = this.props;
        if (historyMode) {
            return false;
        }

        switch (node.type) {
            case "Process":
            case "RiskDirectory":
                return false;
            case "ExpandedRiskNode": {
                const { editEntityFields } = rootNode.content.actions;
                if (!editEntityFields.includes(node.risk.id)) {
                    return false;
                } else {
                    // Using createLocalNode is not ideal, but it's the only place we have the list of entities with edit access.
                    const { createLocalNode } =
                        riskDirectoryView.content.actions;
                    switch (createLocalNode.type) {
                        case "ForAllEntities":
                            return true;
                        case "ForEntities":
                            return createLocalNode.entity.includes(
                                node.entityId
                            );
                        default:
                            return assertNever(createLocalNode);
                    }
                }
            }
            default:
                assertNever(node);
        }
    };

    // Cache for getMapPath
    // The Guid key is the last ancestor id of a given node, allowing all siblings to share the same entry
    //
    // There is no need for a cleanup mechanism here because structural changes and name changes only happens in risk directory screen
    // and the whole cache is wiped-out on tab change.
    mapPathCache: Record<Guid, string[]> = {};

    private historyInfo = () => {
        const { mode } = this.props;
        switch (mode.type) {
            case "Map": {
                return { type: "Map", isHistorical: false };
            }
            case "HistoricalMap": {
                return {
                    type: "HistoricalMap",
                    isHistorical: true,
                    targetDate: mode.targetDate ?? null,
                };
            }
            case "InsightsMap": {
                return {
                    type: "InsightsMap",
                    isHistorical: false,
                    fromDate: mode.fromDate ?? null,
                    toDate: mode.toDate ?? null,
                };
            }
            default: {
                return assertNever(mode);
            }
        }
    };

    handleAtDateChange = (date: Moment | null) => {
        if (date === null) {
            this.handleDateChange({ type: "UNSET" });
        } else {
            this.handleDateChange({ type: "FROM_DATE_PICKER", date });
        }
    };

    handleInsightsCalendarChange = (
        values: [Moment | null, Moment | null] | null
    ) => {
        this.setState({ insightsEditorDates: values });
    };

    handleInsightsOpenChange = (open: boolean) => {
        if (!open) {
            this.setState({ insightsEditorDates: null });
            return;
        }
        if (this.props.mode.type === "InsightsMap") {
            this.setState({
                insightsEditorDates: [
                    this.props.mode.fromDate,
                    this.props.mode.toDate,
                ],
            });
        }
    };

    isDisabledInsightsDate = (current: Moment | null) => {
        if (this.isDisabledAtDate(current)) {
            return true;
        }
        if (current === null) {
            return false;
        }

        return (
            this.state.insightsEditorDates?.some(
                (date) => date !== null && current.isSame(date, "day")
            ) ?? false
        );
    };

    handleInsightsRangeChange = (
        dates: [Moment | null, Moment | null] | null
    ) => {
        if (!dates || !dates[0] || !dates[1]) {
            return;
        }
        const url = new URL(
            window.location.origin +
                window.location.pathname +
                this.props.location.search +
                window.location.hash
        );
        const { searchParams } = url;
        searchParams.set(
            RISK_DIRECTORY_FROM_DATE_SEARCH_PARAM,
            this.getTimestampOnDate(dates[0])!.endOf("day").toISOString(true)
        );
        searchParams.set(
            RISK_DIRECTORY_TO_DATE_SEARCH_PARAM,
            moment(dates[1]).endOf("day").toISOString(true)
        );
        this.props.navigate({
            search: "?" + searchParams.toString(),
            hash: RISK_MAP_INSIGHTS_HASH_OLD,
        });
        this.setState({ aiSummaryEnabled: false });
    };

    render = () => {
        const { drawer } = this.state;
        const {
            riskDirectoryView,
            rootNode,
            entityList,
            common,
            mode,
            focusedEntity,
        } = this.props;

        const historyInfo = this.historyInfo();
        const historyMode = historyInfo.isHistorical;
        let historyDate = null;
        if (historyInfo.type === "HistoricalMap") {
            historyDate = historyInfo.targetDate;
        } else if (historyInfo.type === "InsightsMap") {
            historyDate = historyInfo.fromDate;
        }

        const asyncDataViewIndex =
            mode.type === "InsightsMap"
                ? this.mapDataViewIndex(
                      "insightMerge",
                      riskDirectoryView,
                      entityList,
                      mode.mergedCommon
                  )(rootNode)
                : this.mapDataViewIndex(
                      "map",
                      riskDirectoryView,
                      entityList
                  )(rootNode);

        const entityLevelMetadataColumns = common
            .metadataColumns(riskDirectoryView)
            .entityLevel.filter(
                (col) =>
                    col.fields.Risk !== undefined &&
                    col.fields.Risk.fieldType.type !== "TextArea"
            );
        const rdLevelProcessMetadataColumns = common
            .metadataColumns(riskDirectoryView)
            .riskDirectoryLevel.filter(
                (col) =>
                    col.fields.Process !== undefined &&
                    col.fields.Process.fieldType.type !== "TextArea"
            );
        const rdLevelRiskMetadataColumns = common
            .metadataColumns(riskDirectoryView)
            .riskDirectoryLevel.filter(
                (col) =>
                    col.fields.Risk !== undefined &&
                    col.fields.Risk.fieldType.type !== "TextArea"
            );
        const columnBuilders = this.mapColumnBuilders("map", riskDirectoryView);

        const canEditNode = (
            node: R.ProcessNode | T.ExpandedRiskNode | T.MapNode
        ) => this.canEditNode(node, historyMode);

        const renderError = (code: number | undefined) =>
            this.withApplicationBar(
                undefined,
                code === 403 ? (
                    <NoAccessCard />
                ) : (
                    <LoggedInHTTPError code={code} />
                )
            );
        const renderLoading = () => <PageSpinner />;

        const colsByLevel = common.metadataColumns(riskDirectoryView);
        const customColumns = colsByLevel.riskDirectoryLevel.concat(
            colsByLevel.entityLevel
        );

        const renderContent = ({
            pageState: persistentPageState,
            onPageStateChange: onPersistentPageStateChange,
            onCreateShareableLink,
        }: RenderContentArgs<RiskMapPageStateRaw>) => {
            const pageState =
                persistentPageState !== undefined
                    ? persistentPageState
                    : this.state.pageState;
            const baseOnPageStateChange =
                onPersistentPageStateChange !== undefined
                    ? onPersistentPageStateChange
                    : (pageState: RiskMapPageStateRaw) =>
                          this.setState({ pageState });
            const onPageStateChange = (newPageState: RiskMapPageStateRaw) => {
                if (
                    newPageState.showResidualHeatmap !==
                    pageState?.showResidualHeatmap
                ) {
                    this.setState({ aiSummaryEnabled: false });
                }
                baseOnPageStateChange(newPageState);
            };

            const focusCells =
                pageState.focusFilterCoords.length > 0
                    ? pageState.focusFilterCoords
                    : null;

            const setFocusCells = (cells: CellCoord[] | null) => {
                onPageStateChange({
                    ...pageState,
                    focusFilterCoords: cells || [],
                });
            };

            const onRemoveFocusFilter = (
                _id: string,
                filter: SimpleFieldFilter
            ) => {
                const filterWithContext = filter as SimpleFieldFilter &
                    CellCoord; // It's more robust than parsing the value string, IMO
                if (focusCells === null) {
                    return;
                }
                const newFocusCells = focusCells.filter(
                    ({ x, y }) =>
                        filterWithContext.x !== x && filterWithContext.y !== y
                );
                setFocusCells(newFocusCells.length > 0 ? newFocusCells : null);
            };

            const clearFocusFilter = () => {
                setFocusCells(null);
            };

            const mapMode = pageState?.showResidualHeatmap
                ? RESIDUAL_MODE
                : INHERENT_MODE;

            const focusFilter = this.focusFilter(
                "map",
                riskDirectoryView,
                mapMode,
                focusCells
            );

            const {
                filter,
                onFilterChange,
                visibleColumnIds,
                onVisibleColumnsChange,
            } = splitPageStateWithCallback(pageState, onPageStateChange);
            const queryResult = this.mapQueryResult(
                "map",
                riskDirectoryView,
                rootNode,
                filter,
                entityList
            );
            const focusDataViewIndex = this.focusDataViewIndex(
                "map",
                riskDirectoryView,
                filter,
                entityList
            )(rootNode);
            const focusQueryResult = this.focusQueryResult(
                "map",
                riskDirectoryView,
                rootNode,
                filter,
                focusCells,
                entityList,
                mapMode
            );

            // For risk insights we need to use the unfiltered views as source of truth for comparison purposes
            const endDataView =
                this.props.mode.type === "InsightsMap"
                    ? this.mapDataView(
                          "map",
                          riskDirectoryView,
                          rootNode,
                          entityList
                      )
                    : undefined;

            const startDataView =
                this.props.mode.type === "InsightsMap"
                    ? this.mapDataView(
                          "insightStart",
                          this.props.mode.diffData.riskDirData!,
                          this.props.mode.diffData.riskNodesData!,
                          entityList,
                          this.props.mode.diffCommon
                      )
                    : undefined;

            // And we show nodes that are in at least one focus view
            const visibleInsightNodeIds =
                this.props.mode.type === "InsightsMap"
                    ? Promise.all([
                          this.focusQueryResult(
                              "insightStart",
                              this.props.mode.diffData.riskDirData!,
                              this.props.mode.diffData.riskNodesData!,
                              filter,
                              focusCells,
                              entityList,
                              mapMode,
                              this.props.mode.diffCommon
                          ),
                          focusQueryResult,
                      ]).then(([startQueryResult, endQueryResult]) => {
                          const visibleInsightNodeIds = new Set<string>();

                          DV.forEach(
                              startQueryResult.dataView.dataSource.iter(),
                              (node) => visibleInsightNodeIds.add(node.value.id)
                          );
                          DV.forEach(
                              endQueryResult.dataView.dataSource.iter(),
                              (node) => visibleInsightNodeIds.add(node.value.id)
                          );

                          return visibleInsightNodeIds;
                      })
                    : undefined;

            const {
                switchableViewMode,
                onSwitchableViewModeChange,
                visibleColumnIdsForTableView,
                onVisibleColumnsForTableViewChanged,
                visibleColumnIdsForTreeView,
                onVisibleColumnsForTreeViewChanged,
            } = computeArgsForSwitchableTreeView(
                pageState,
                onPageStateChange,
                visibleColumnIds,
                onVisibleColumnsChange
            );

            return this.withApplicationBar(
                onCreateShareableLink,
                <ContentWithoutBorder
                    className={theme.scrollableBorderedContainer}
                >
                    <div
                        className={
                            theme.searchBar + " " + theme.stickySearchBar
                        }
                    >
                        {historyMode && (
                            <>
                                <div className={theme.datePickerContainer}>
                                    <div className={theme.datePicker}>
                                        <DatePicker
                                            onChange={this.handleAtDateChange}
                                            disabledDate={this.isDisabledAtDate}
                                            defaultValue={
                                                historyDate ?? undefined
                                            }
                                            size="large"
                                        />
                                    </div>
                                </div>
                                <div className={theme.datePickerTooltip}>
                                    <InfoTooltip
                                        text={t(
                                            "RiskMap_History_Date_Explanation"
                                        )}
                                    />
                                </div>
                            </>
                        )}
                        {this.props.mode.type === "InsightsMap" && (
                            <>
                                <div
                                    className={cx(
                                        theme.datePickerContainer,
                                        theme.insightsDatePickerContainer
                                    )}
                                >
                                    <AntdDatePicker.RangePicker
                                        value={[
                                            this.props.mode.fromDate,
                                            this.props.mode.toDate,
                                        ]}
                                        onChange={
                                            this.handleInsightsRangeChange
                                        }
                                        onCalendarChange={
                                            this.handleInsightsCalendarChange
                                        }
                                        onOpenChange={
                                            this.handleInsightsOpenChange
                                        }
                                        disabledDate={
                                            this.isDisabledInsightsDate
                                        }
                                        allowClear={false}
                                        size="large"
                                        format="DD-MM-YYYY"
                                    />
                                </div>
                                <div className={theme.datePickerTooltip}>
                                    <InfoTooltip
                                        text={t(
                                            "RiskMap_Insights_Date_Explanation"
                                        )}
                                    />
                                </div>
                            </>
                        )}
                        <div className={theme.filters}>
                            <AsyncFilter<DV.NodeDescriptor<T.MapNode>>
                                id="risk_map_filter"
                                hideResultCount
                                extractorRefs={[
                                    { extractorId: RISK_EXTRACTOR_KEYS.RISK },
                                    {
                                        extractorId:
                                            RISK_EXTRACTOR_KEYS.PROCESSES,
                                    },
                                    ...rdLevelProcessMetadataColumns.map(
                                        (col) => ({
                                            extractorId:
                                                U.prefixFilterMetadataExtractor(
                                                    col.id,
                                                    "Process"
                                                ),
                                        })
                                    ),
                                    ...rdLevelRiskMetadataColumns.map(
                                        (col) => ({
                                            extractorId:
                                                U.prefixFilterMetadataExtractor(
                                                    col.id,
                                                    "Risk"
                                                ),
                                        })
                                    ),
                                    ...entityLevelMetadataColumns.map(
                                        (col) => ({
                                            extractorId:
                                                U.prefixFilterMetadataExtractor(
                                                    col.id,
                                                    "Risk"
                                                ),
                                        })
                                    ),
                                    {
                                        extractorId:
                                            RISK_EXTRACTOR_KEYS.EXPANDED_RISK_NODE_ENTITY,
                                    },
                                    {
                                        extractorId:
                                            RISK_EXTRACTOR_KEYS.CONTROLS,
                                    },
                                    {
                                        extractorId:
                                            RISK_EXTRACTOR_KEYS.CONTROL_PROGRAMS,
                                    },
                                    {
                                        extractorId:
                                            RISK_EXTRACTOR_KEYS.FILTER_SCOPE,
                                    },
                                    {
                                        extractorId:
                                            RISK_EXTRACTOR_KEYS.ASSESSMENT_STATUS,
                                    },
                                ]}
                                onAddFilter={this.addFilter(
                                    filter,
                                    onFilterChange
                                )}
                                onRemoveFilter={this.removeFilter(
                                    filter,
                                    onFilterChange
                                )}
                                onResetFilter={this.resetFilter(
                                    filter,
                                    onFilterChange
                                )}
                                placeholder={t("Search")}
                                queryResult={queryResult}
                                dataViewIndex={asyncDataViewIndex}
                                filter={filter}
                            />
                            <AsyncFilter<DV.NodeDescriptor<T.MapNode>>
                                id="risk_map_focus_filter"
                                hideResultCount
                                hideTypeahead
                                extractorRefs={[{ extractorId: "focus" }]}
                                onAddFilter={clearFocusFilter} // Should never be called
                                onRemoveFilter={onRemoveFocusFilter}
                                onResetFilter={clearFocusFilter}
                                queryResult={focusQueryResult}
                                dataViewIndex={focusDataViewIndex}
                                filter={focusFilter}
                            />
                        </div>
                    </div>
                    <RiskHeatMap
                        getMode={this.getMode}
                        buildFocusQueryResult={(...args) =>
                            this.props.mode.type === "InsightsMap"
                                ? this.buildFocusQueryResult(
                                      "insightMerge",
                                      ...args,
                                      this.props.mode.mergedCommon
                                  )
                                : this.buildFocusQueryResult("map", ...args)
                        }
                        buildMapQueryResult={(...args) =>
                            this.props.mode.type === "InsightsMap"
                                ? this.buildMapQueryResult(
                                      "insightMerge",
                                      ...args,
                                      this.props.mode.mergedCommon
                                  )
                                : this.buildMapQueryResult("map", ...args)
                        }
                        buildNodeTypeToNodeExtractors={
                            this.buildNodeTypeToNodeExtractors
                        }
                        focusCells={focusCells}
                        entityList={entityList}
                        mode={this.props.mode}
                        onPageStateChange={onPageStateChange}
                        pageState={pageState}
                        riskDirectoryView={riskDirectoryView}
                        rootNode={rootNode}
                        filter={filter}
                        setFocusCells={setFocusCells}
                    />

                    {this.props.mode.type === "InsightsMap" &&
                        this.props.mode.fromDate &&
                        this.props.mode.toDate && (
                            <RiskMapAISummary
                                key={`${this.props.mode.fromDate.format("YYYY-MM-DD")}/${this.props.mode.toDate.format("YYYY-MM-DD")}`}
                                riskDirectoryId={
                                    riskDirectoryView.content.resource.id
                                }
                                kind={
                                    mapMode === RESIDUAL_MODE
                                        ? "Residual"
                                        : "Inherent"
                                }
                                from={this.props.mode.fromDate.format(
                                    "YYYY-MM-DD"
                                )}
                                to={this.props.mode.toDate.format("YYYY-MM-DD")}
                                enabled={this.state.aiSummaryEnabled}
                                onEnable={() =>
                                    this.setState({
                                        aiSummaryEnabled: true,
                                    })
                                }
                            />
                        )}
                    {this.props.mode.type === "InsightsMap" && (
                        <DeferredContent
                            promise={Promise.all([
                                // eslint-disable-next-line @typescript-eslint/await-thenable -- deliberate: Promise.all bundles the two synchronous data views with the async ids so all three land in DeferredContent's state as one consistent snapshot. Passing only the promise would pair stale ids with current views during a RELOAD.
                                startDataView!,
                                // eslint-disable-next-line @typescript-eslint/await-thenable -- see above
                                endDataView!,
                                visibleInsightNodeIds!,
                            ])}
                            renderContent={([
                                startDataView,
                                endDataView,
                                visibleInsightNodeIds,
                            ]) => (
                                <RiskInsightsTable
                                    startDataView={startDataView}
                                    startDateRootNode={
                                        this.props.mode.type === "InsightsMap"
                                            ? this.props.mode.diffData
                                                  .riskNodesData
                                            : undefined
                                    }
                                    startDateRiskDirectoryView={
                                        this.props.mode.type === "InsightsMap"
                                            ? this.props.mode.diffData
                                                  .riskDirData
                                            : undefined
                                    }
                                    endDataView={endDataView}
                                    endDateRootNode={rootNode}
                                    endDateRiskDirectoryView={riskDirectoryView}
                                    visibleNodeIds={visibleInsightNodeIds}
                                    mapMode={mapMode}
                                />
                            )}
                        />
                    )}

                    {this.props.mode.type !== "InsightsMap" && (
                        <DeferredContent
                            promise={focusQueryResult}
                            renderContent={(focusQueryResult) => (
                                <SwitchableTreeView
                                    id="risk_map_view"
                                    dataView={focusQueryResult.dataView}
                                    visibleColumnIdsForTreeView={
                                        visibleColumnIdsForTreeView
                                    }
                                    onVisibleColumnsForTreeViewChanged={
                                        onVisibleColumnsForTreeViewChanged
                                    }
                                    visibleColumnIdsForTableView={
                                        visibleColumnIdsForTableView
                                    }
                                    onVisibleColumnsForTableViewChanged={
                                        onVisibleColumnsForTableViewChanged
                                    }
                                    defaultMode={switchableViewMode}
                                    onModeChange={onSwitchableViewModeChange}
                                    selectionHandler={{
                                        canSelect: (
                                            nodeDescriptor: DV.NodeDescriptor<T.MapNode>
                                        ) => canEditNode(nodeDescriptor.value),
                                        tools: [
                                            {
                                                isEnabled:
                                                    this.canUnassessNodes,
                                                type: "Text",
                                                label: t(
                                                    "RiskMap_List_Action_Unassess"
                                                ),
                                                onCommand: this.onNodeUnassess,
                                            },
                                            {
                                                isEnabled: this.canAssessNodes,
                                                type: "Text",
                                                label: t(
                                                    "RiskMap_List_Action_Assess"
                                                ),
                                                onCommand: this.onNodeAssess,
                                            },
                                        ],
                                    }}
                                    dataViewModifier={{
                                        [Mode.TABLE]: (dataView) => {
                                            const filteredDataSource =
                                                new DV.ArrayDataSource(
                                                    DV.toArray(
                                                        dataView.dataSource.iter()
                                                    ),
                                                    (n) =>
                                                        dataView.dataSource.getId(
                                                            n
                                                        ),
                                                    [
                                                        (n) =>
                                                            n.value.type ===
                                                            "ExpandedRiskNode",
                                                    ]
                                                );
                                            return {
                                                ...dataView,
                                                dataSource: filteredDataSource,
                                            };
                                        },
                                    }}
                                    openRoots
                                    hideRoots
                                    columnBuilders={columnBuilders}
                                    rowClickHandler={{
                                        type: "ActiveRowClickHandler",
                                        activeRowId: drawer && drawer.nodeId,
                                        onClick: this.handleActiveNodeChanged,
                                    }}
                                    maxHeight={500}
                                    calcMinTableHeight={this.calcMinTableHeight}
                                />
                            )}
                        />
                    )}
                    {this.renderDrawer()}
                    {this.renderDialog(entityLevelMetadataColumns)}
                </ContentWithoutBorder>
            );
        };

        const isEnabled = mode.type === "Map";
        return (
            <ConditionalPageStateManager<"RiskMap", RiskMapPageStateRaw>
                pageKey={PAGE_KEY.riskMap(
                    riskDirectoryView.content.resource.id,
                    focusedEntity.type === "FOCUSED"
                        ? focusedEntity.entityId
                        : undefined
                )}
                codecs={pageStateRiskMapsCodecs(customColumns)}
                isEnabled={isEnabled}
                renderLoading={renderLoading}
                renderError={renderError}
                renderContent={renderContent}
            />
        );
    };

    calcMinTableHeight = () => {
        return Math.round(window.innerHeight / 2);
    };

    exportRiskMatrix = (options: RiskMatrixExportOptions) => {
        // Generates the data to export
        const exportableArray = this.buildExportableRiskMatrix(options);

        // Creates the XLSX Worksheet
        const metadata = newExportMetadata();
        const worksheet = XLSXUtils.tableToWorksheet(
            exportableArray,
            undefined,
            metadata
        );

        if (options.asCSV) {
            const binary = XLSXUtils.worksheetToCSVBinary(worksheet);
            // Downloads it
            const blob = new Blob([binary], {
                type: "text/csv;charset=utf-8",
            });
            saveAs(blob, formatFileNameForExport("RiskMatrix", "csv"));
        } else {
            const workbook = XLSXUtils.workbookWithSingleWorksheet(
                "Risk Matrix",
                worksheet
            );
            const data = XLSXUtils.workbookToBinary(workbook);

            // Downloads it
            const blob = new Blob([data], {
                type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            });
            saveAs(blob, formatFileNameForExport("RiskMatrix", "xlsx"));
        }
    };

    openRiskMatrixExportPopup = () => {
        this.setState({
            popupState: { type: "ExportRiskMatrix" },
        });
    };

    withApplicationBar = (
        onCreateShareableLink: (() => void) | undefined,
        children: React.ReactNode
    ) => {
        const { riskDirectoryView, mode, focusedEntity, navigate } = this.props;

        const actions: Array<ActionSpec> = [];

        if (onCreateShareableLink) {
            actions.push({
                id: "shareable-link-create-new",
                label: t("ShareableLink_CreateNew"),
                icon: { svg: shareIcon },
                onClick: onCreateShareableLink,
            });
        }
        const exportAction = {
            id: "risk_map_export_action",
            label: t("RiskMaps_Export"),
            icon: { svg: IconExport },
            onClick: this.openRiskMatrixExportPopup,
        };

        let currentTabId: string;
        switch (mode.type) {
            case "Map":
                currentTabId = T.RISK_MAP_TAB;
                actions.push({
                    id: "risk_map_goto_risk_dir",
                    label: t("RiskDirectory_View_Tab_Risk_Directory"),
                    icon: { svg: IconRiskDir },
                    link: { to: `#${T.RISK_DIRECTORY_TAB}` },
                });
                actions.push(exportAction);
                break;
            case "HistoricalMap":
                currentTabId = T.RISK_MAP_HISTORY_TAB;

                actions.push(exportAction);

                break;
            case "InsightsMap":
                currentTabId = T.RISK_MAP_INSIGHTS_TAB_OLD;
                break;
            default:
                return assertNever(mode);
        }

        const getTitle = () => {
            const riskModuleBreadCrumb = {
                label: t("RiskDirectory_RiskManager"),
                url: RISK_MODULE,
            };
            const { title: riskDirectoryTitle, id: riskDirectoryId } =
                riskDirectoryView.content.resource;
            switch (focusedEntity.type) {
                case "FOCUSED":
                    const focusedEntityResource =
                        this.props.rootNode.linkedResources.entities.find(
                            (entity) => entity.id === focusedEntity.entityId
                        );
                    if (!focusedEntityResource) {
                        throw new Error(
                            "Expected to find directory associated entity"
                        );
                    }
                    return {
                        title: focusedEntityResource.name,
                        breadCrumbs: [
                            riskModuleBreadCrumb,
                            {
                                label: riskDirectoryTitle,
                                url: `${RISK_MODULE}/${riskDirectoryId}`,
                            },
                        ],
                    };
                case "NONE":
                    return {
                        title: riskDirectoryTitle,
                        breadCrumbs: [riskModuleBreadCrumb],
                    };
                default:
                    assertNever(focusedEntity);
            }
        };
        const tabSpecs: TabSpec[] = [
            {
                id: T.RISK_MAP_TAB,
                label: t("RiskDirectory_View_Tab_Risk_Map"),
                onClick: () => {
                    if (this.props.mode.type === "Map") {
                        return;
                    }
                    const params = new URLSearchParams(
                        this.props.location.search
                    );
                    navigate({
                        search: `?${params}`,
                        hash: T.RISK_MAP_TAB,
                    });
                },
            },
            {
                id: T.RISK_MAP_INSIGHTS_TAB,
                label: t("RiskDirectory_View_Tab_Risk_Insights"),
                onClick: () => {
                    if (this.props.mode.type === "InsightsMap") {
                        return;
                    }
                    const params = new URLSearchParams(
                        this.props.location.search
                    );

                    // Preserve the user's last picked Insights range — only fall
                    // back to defaults if from/to are not already in the URL.
                    const hasExistingRange =
                        params.has(RISK_DIRECTORY_FROM_DATE_SEARCH_PARAM) &&
                        params.has(RISK_DIRECTORY_TO_DATE_SEARCH_PARAM);
                    if (!hasExistingRange) {
                        const toDate = moment().endOf("day");
                        const oneYearAgo = moment()
                            .subtract(1, "year")
                            .endOf("day");
                        const oldestValidStart =
                            this.props.rootNode.content.resource.riskMapValidity.reduce(
                                (oldest: Moment | null, interval) => {
                                    const start = interval.start
                                        ? moment(interval.start)
                                        : null;
                                    if (start === null) {
                                        return oldest;
                                    }
                                    if (
                                        oldest === null ||
                                        start.isBefore(oldest)
                                    ) {
                                        return start;
                                    }
                                    return oldest;
                                },
                                null
                            );
                        const canUseOneYearAgo =
                            !this.isDisabledAtDate(oneYearAgo);
                        const fromDate =
                            canUseOneYearAgo || !oldestValidStart
                                ? oneYearAgo
                                : moment(oldestValidStart).endOf("day");
                        params.set(
                            RISK_DIRECTORY_FROM_DATE_SEARCH_PARAM,
                            fromDate.toISOString(true)
                        );
                        params.set(
                            RISK_DIRECTORY_TO_DATE_SEARCH_PARAM,
                            toDate.toISOString(true)
                        );
                    }

                    navigate({
                        search: `?${params}`,
                        hash: T.RISK_MAP_INSIGHTS_TAB,
                    });
                },
            },
            {
                id: T.RISK_MAP_HISTORY_TAB,
                label: t("RiskDirectory_View_Tab_Risk_Map_History"),
                onClick: () => {
                    if (this.props.mode.type === "HistoricalMap") {
                        return;
                    }
                    const params = new URLSearchParams(
                        this.props.location.search
                    );
                    navigate({
                        search: `?${params}`,
                        hash: T.RISK_MAP_HISTORY_TAB,
                    });
                },
            },
        ].filter((tabSpec) => {
            if (tabSpec.id === T.RISK_MAP_INSIGHTS_TAB_OLD) {
                return this.props.riskInsightsEnabled;
            }
            return true;
        });
        const navigation = {
            type: "Tabs" as const,
            currentTabId,
            tabSpecs,
        };

        return (
            <OrgApplicationBar
                contentClassName={theme.riskMapContent}
                title={getTitle()}
                navigation={navigation}
                actions={{
                    id: "risk_map_actions",
                    actions,
                }}
            >
                {children}
            </OrgApplicationBar>
        );
    };

    buildExportableRiskMatrix = (
        exportOptions: RiskMatrixExportOptions
    ): ExportableArray => {
        const { riskDirectoryView, rootNode, entityList, common, allControls } =
            this.props;
        return exportRiskMatrix(
            this.mapDataView("map", riskDirectoryView, rootNode, entityList),
            common
                .metadataColumns(riskDirectoryView)
                .riskDirectoryLevel.map((mdCol) => mdCol.id),
            common
                .metadataColumns(riskDirectoryView)
                .entityLevel.map((mdCol) => mdCol.id),
            rootNode,
            allControls,
            exportOptions
        );
    };

    renderDialog = (
        entityLevelMetadataColumns: T.MetadataColumnWithId<MetadataColumnId>[]
    ) => {
        const { popupState } = this.state;
        if (popupState === undefined) {
            return null;
        }
        switch (popupState.type) {
            case "FailedAssessment":
                // This is a temporary solution. Once we fix backend we would like to have it uncommented and working
                const dataView = popupState.formattedView.dataView;
                const globalfilterIds = new Map(
                    popupState.failingNodes.global_nodes.map((node) => [
                        `${node.node_id}-${node.entity_id}`,
                        node.missing,
                    ])
                );
                const localFilterIds = new Map(
                    Object.entries(popupState.failingNodes.local_nodes)
                );
                const extractorRisk = DV.getExtractor(dataView, "risk");
                const extractorEntities = DV.getExtractor(dataView, "entities");
                const extractorScope = DV.getExtractor(dataView, "scope");
                const descriptions: NodeWithMissingColumns[] = DV.toArray(
                    dataView.dataSource.iter()
                ).reduce((filtered: NodeWithMissingColumns[], item) => {
                    switch (item.value.type) {
                        case "ExpandedRiskNode":
                            const scope: T.NodeScope =
                                extractorScope.getValue(item);
                            const nodeId = item.value.risk.id;
                            switch (scope.scope) {
                                case "Global": {
                                    const entities: number[] =
                                        extractorEntities.getValue(item);
                                    for (const entity of entities) {
                                        const key = `${nodeId}-${entity}`;

                                        if (globalfilterIds.has(key)) {
                                            const missing =
                                                globalfilterIds.get(key)!;
                                            filtered.push({
                                                item,
                                                missing,
                                            });
                                        }
                                    }
                                    break;
                                }
                                case "Local": {
                                    if (localFilterIds.has(nodeId)) {
                                        const missing =
                                            localFilterIds.get(nodeId)!;
                                        filtered.push({
                                            item,
                                            missing,
                                        });
                                    }
                                    break;
                                }
                                default:
                                    assertNever(scope);
                            }
                            break;
                        case "Process":
                        case "RiskDirectory":
                            break;
                        default:
                            assertNever(item.value);
                    }
                    return filtered;
                }, []);

                return (
                    <FailedAssessmentModal<NodeWithMissingColumns>
                        title={t("RiskMaps_FailedToAssess")}
                        body={t("RiskMaps_FailedToAssessBody")}
                        onClickOk={this.closePopup}
                        message={(item) => {
                            const risk = extractorRisk.asString(item.item);
                            const entity = extractorEntities.asString(
                                item.item
                            );
                            const missing = item.missing
                                .map(
                                    (missing) =>
                                        entityLevelMetadataColumns.find(
                                            (col) =>
                                                col.fields.Risk?.id === missing
                                        )?.name
                                )
                                .join(", ");
                            return t("RiskMaps_FailedToAssessRow", {
                                risk,
                                entity,
                                missing,
                            });
                        }}
                        descriptions={descriptions}
                        descriptionSorter={(a, b) =>
                            extractorRisk.compare(a.item, b.item)
                        }
                    />
                );
            case "ExportRiskMatrix":
                return (
                    <ExportRiskMatrixPopup
                        onExportMatrix={this.exportRiskMatrix}
                        onCancel={this.closePopup}
                    />
                );
            default:
                return assertNever(popupState);
        }
    };

    closePopup = () => {
        this.setState({ popupState: undefined });
    };

    renderDrawer = () => {
        if (this.state.drawer) {
            const {
                riskDirectoryView,
                entityList,
                rootNode,
                allControls,
                responseEnumMap,
                updateNode,
                common,
                ajaxManager,
                orgProfileCookie,
                updateRootNodeLinkedResources,
            } = this.props;
            const { nodeId } = this.state.drawer;

            const historyInfo = this.historyInfo();
            const historyMode = historyInfo.isHistorical;
            let historyDate = null;
            if (historyInfo.type === "HistoricalMap") {
                historyDate = historyInfo.targetDate ?? null;
            } else if (historyInfo.type === "InsightsMap") {
                historyDate = historyInfo.fromDate ?? null;
            }

            const nodeDesc = this.mapDrawerNode(
                "map",
                riskDirectoryView,
                rootNode,
                entityList,
                nodeId
            );
            if (!nodeDesc) {
                console.error("Cannot find drawer node " + nodeId);
                this.setState({ drawer: undefined });
                return null;
            }
            const node: T.MapNode = nodeDesc.value;
            const nodeTypeToExtractors = this.nodeTypeToNodeExtractors(
                "map",
                riskDirectoryView,
                rootNode.linkedResources.blobs
            );
            const canEditNode = (node: R.ProcessNode | T.ExpandedRiskNode) =>
                this.canEditNode(node, historyMode);

            let metadataExtractors: Array<
                DV.DataExtractor<T.NodeWithMD, T.MetadataInstance>
            >;
            switch (node.type) {
                case "Process":
                    metadataExtractors = nodeTypeToExtractors[node.type];
                    break;
                case "ExpandedRiskNode":
                    metadataExtractors = nodeTypeToExtractors["Risk"];
                    break;
                case "RiskDirectory":
                    return null;
                default:
                    return assertNever(node);
            }

            return (
                <MapNodeDrawer
                    riskDirectoryView={riskDirectoryView}
                    rootNode={rootNode}
                    node={node}
                    metadataExtractors={metadataExtractors}
                    responseEnumMap={responseEnumMap}
                    allControls={allControls}
                    updateNode={updateNode}
                    common={common}
                    historyMode={historyMode}
                    historyDate={historyDate ?? null}
                    ajaxManager={ajaxManager}
                    orgProfileCookie={orgProfileCookie}
                    entities={common.entities(entityList, rootNode)}
                    onDateChange={this.handleDateChange}
                    onClose={this.handleCloseDrawer}
                    canEditNode={canEditNode}
                    updateRootNodeLinkedResources={
                        updateRootNodeLinkedResources
                    }
                />
            );
        } else {
            return null;
        }
    };

    handleDateChange = (dateChange: DateChange) => {
        const url = new URL(
            window.location.origin +
                window.location.pathname +
                this.props.location.search +
                window.location.hash
        );
        const { searchParams } = url;
        url.hash = RISK_MAP_HISTORY_HASH;
        switch (dateChange.type) {
            case "UNSET":
                searchParams.delete(RISK_DIRECTORY_DATE_SEARCH_PARAM);
                break;
            case "FROM_DATE_PICKER":
                const setDate = this.getTimestampOnDate(dateChange.date);
                searchParams.set(
                    RISK_DIRECTORY_DATE_SEARCH_PARAM,
                    setDate!.toISOString(true)
                );
                break;
            case "FROM_EXACT_TIMESTAMP":
                // Because frontend doesn't support microsecond precision, but backend does
                const momentRounded = moment(dateChange.date).add(
                    1,
                    "milliseconds"
                );
                searchParams.set(
                    RISK_DIRECTORY_DATE_SEARCH_PARAM,
                    momentRounded.toISOString(true)
                );
                break;
            default:
                assertNever(dateChange);
        }

        this.props.navigate({
            search: "?" + searchParams.toString(),
            hash: RISK_MAP_HISTORY_HASH,
        });
    };

    getTimestampOnDate = (date: Moment): Moment | null => {
        const day = {
            start: moment(date).startOf("day"),
            end: moment(date).endOf("day"),
        };

        // this interval overlaps with the day, but the interval
        // might end later than the day itself
        const interval = this.getOverlappingInterval(day);

        if (interval !== null) {
            if (interval.end !== null) {
                // subtract 1ms to make sure the result is within the interval
                const end = moment(interval.end).subtract(1, "ms");
                // choose the earliest one
                return moment.min(end, day.end);
            }
            // the interval has no end
            return day.end;
        }
        return null;
    };

    getOverlappingInterval = (
        day: Interval
    ): ResourceValidityInterval | null => {
        const today = moment().endOf("day");
        const { riskMapValidity } = this.props.rootNode.content.resource;
        return (
            riskMapValidity.find((interval) => {
                const intervalStart = interval.start ?? HISTORY_FEATURE_START;
                const intervalEnd = interval.end ?? today;
                return intervalsOverlap(day, {
                    start: moment(intervalStart),
                    end: moment(intervalEnd),
                });
            }) ?? null
        );
    };

    isDisabledAtDate = (date: Moment | null) => {
        if (date === null) {
            return false;
        }

        const day = {
            start: moment(date).startOf("day"),
            end: moment(date).endOf("day"),
        };

        const today = moment().endOf("day");

        if (day.end.isAfter(today)) {
            return true;
        }

        if (day.end.isBefore(HISTORY_FEATURE_START)) {
            return true;
        }

        // A date is enabled if the risk map has something to
        // show on that day; i.e. does the day overlap with any
        // of the risk map validity intervals
        const timestamp = this.getOverlappingInterval(day);
        const isEnabled = timestamp !== null;
        return !isEnabled;
    };

    private addFilter =
        (
            oldFilter: SimpleAndFilter | null,
            onFilterChange:
                | ((filter: SimpleAndFilter | null) => void)
                | undefined
        ) =>
        (extractorId: string, filter: SimpleFieldFilter, excluded: boolean) => {
            if (onFilterChange) {
                onFilterChange(
                    addFieldFilter(extractorId, filter, excluded, oldFilter)
                );
            }
        };

    private removeFilter =
        (
            oldFilter: SimpleAndFilter | null,
            onFilterChange:
                | ((filter: SimpleAndFilter | null) => void)
                | undefined
        ) =>
        (extractorId: string, filter: SimpleFieldFilter, excluded: boolean) => {
            if (onFilterChange) {
                onFilterChange(
                    oldFilter &&
                        removeFieldFilter(
                            extractorId,
                            filter,
                            excluded,
                            oldFilter
                        )
                );
            }
        };

    private resetFilter =
        (
            _oldFilter: SimpleAndFilter | null,
            onFilterChange:
                | ((filter: SimpleAndFilter | null) => void)
                | undefined
        ) =>
        () => {
            if (onFilterChange) {
                onFilterChange(null);
            }
        };

    changeAssessment = (
        onSelectionActionDone: () => void,
        formattedView: FormattedView<DV.NodeDescriptor<T.MapNode>>,
        selectedIds: Array<string>,
        assessmentStatus: SetRiskAssessment
    ) => {
        const dataView = formattedView.dataView;
        const { ajaxManager } = this.props;
        const id = this.props.riskDirectoryView.content.resource.id;
        const extractorScope = DV.getExtractor(
            dataView,
            RISK_EXTRACTOR_KEYS.SCOPE
        );
        const extractorEntities = DV.getExtractor(
            dataView,
            RISK_EXTRACTOR_KEYS.ENTITIES
        );
        const extractorAssessment = DV.getExtractor(
            dataView,
            RISK_EXTRACTOR_KEYS.ASSESSMENT_STATUS
        );
        const filterIds = new Set(selectedIds);

        const value = new Map<string, UpdatedRiskDirectoryNode>();
        DV.forEach(
            dataView.dataSource.iter(),
            (item: DV.NodeDescriptor<T.MapNode>) => {
                if (!filterIds.has(dataView.dataSource.getId(item))) {
                    return;
                }

                if (
                    !U.canChangeAssessmentStatus(
                        assessmentStatus,
                        extractorAssessment.getValue(
                            item
                        ) as AssessmentStatus["type"]
                    )
                ) {
                    return;
                }

                switch (item.value.type) {
                    case "RiskDirectory":
                    case "Process":
                        throw new Error(
                            "Risk Directories & Processes don't have content"
                        );
                    case "ExpandedRiskNode":
                        const id = item.value.risk.id;
                        const scope: T.NodeScope =
                            extractorScope.getValue(item);
                        switch (scope.scope) {
                            case "Global":
                                const entities: number[] =
                                    extractorEntities.getValue(item);
                                const entity = entities[0];
                                const entry = value.get(id);
                                if (!entry) {
                                    value.set(id, {
                                        scope: {
                                            type: "UpdatedGlobalWithContent",
                                            entityIdContent: {
                                                [entity]: { assessmentStatus },
                                            },
                                        },
                                    });
                                } else if (
                                    entry.scope?.type ===
                                        "UpdatedGlobalWithContent" &&
                                    entry.scope.entityIdContent
                                ) {
                                    entry.scope.entityIdContent[entity] = {
                                        assessmentStatus,
                                    };
                                }
                                break;
                            case "Local":
                                value.set(id, {
                                    scope: {
                                        type: "UpdatedLocalWithContent",
                                        content: { assessmentStatus },
                                    },
                                });
                                break;
                            default:
                                assertNever(scope);
                        }
                        break;
                    default:
                        assertNever(item.value);
                }
            }
        );

        const doReload = () => {
            emitNotification("success", t("RiskMap_Updated"));
            this.props.reloadDirectory();
            onSelectionActionDone();
        };

        ajaxManager
            .ajax<void, MapActionStatus>(
                () =>
                    patchRisksRiskDirectoryIdNodes(
                        id,
                        Object.fromEntries(value)
                    ),
                {
                    component: this,
                    initialStatusValue: "DEFAULT",
                    inFlightStatus: { type: "UPDATING" },
                    getSuccessStatus: () => ({ type: "DEFAULT" }),
                    getErrorStatus: () => ({ type: "DEFAULT" }),
                    errorHandlingPolicy: "DEFAULT",
                    expectedStatus: [200],
                    onErrorCode: {
                        [STATUS_CODE_ERROR_OPERATION_FAILED]: (
                            error: AxiosError
                        ) => {
                            this.setState({
                                popupState: {
                                    type: "FailedAssessment",
                                    failingNodes: error.response!
                                        .data as MissingRequiredColumnsDetails,
                                    formattedView,
                                },
                            });
                        },
                    },
                }
            )
            .then(doReload)
            .catch((_e) => {});
    };

    onNodeAssess = (
        onSelectionActionDone: () => void,
        formattedView: FormattedView<DV.NodeDescriptor<T.MapNode>>,
        selectedIds: Array<string>
    ) => {
        this.changeAssessment(
            onSelectionActionDone,
            formattedView,
            selectedIds,
            "assessed"
        );
    };

    onNodeUnassess = (
        onSelectionActionDone: () => void,
        formattedView: FormattedView<DV.NodeDescriptor<T.MapNode>>,
        selectedIds: Array<string>
    ) => {
        this.changeAssessment(
            onSelectionActionDone,
            formattedView,
            selectedIds,
            "unassessed"
        );
    };

    canChangeAssessmentForAnyNode = (
        formattedView: FormattedView<DV.NodeDescriptor<T.MapNode>>,
        selectedIds: Array<string>,
        assessmentStatus: SetRiskAssessment
    ) => {
        const dataView = formattedView.dataView;
        const extractorAssessment = DV.getExtractor(
            dataView,
            RISK_EXTRACTOR_KEYS.ASSESSMENT_STATUS
        );
        const filterIds = new Set(selectedIds);

        return (
            DV.find(
                dataView.dataSource.iter(),
                (item: DV.NodeDescriptor<T.MapNode>) => {
                    if (!filterIds.has(dataView.dataSource.getId(item))) {
                        return false;
                    }

                    if (
                        !U.canChangeAssessmentStatus(
                            assessmentStatus,
                            extractorAssessment.getValue(
                                item
                            ) as AssessmentStatus["type"]
                        )
                    ) {
                        return false;
                    }
                    return true;
                }
            ) !== undefined
        );
    };

    canAssessNodes = (
        formattedView: FormattedView<DV.NodeDescriptor<T.MapNode>>,
        selectedIds: Array<string>
    ) =>
        this.canChangeAssessmentForAnyNode(
            formattedView,
            selectedIds,
            "assessed"
        );

    canUnassessNodes = (
        formattedView: FormattedView<DV.NodeDescriptor<T.MapNode>>,
        selectedIds: Array<string>
    ) =>
        this.canChangeAssessmentForAnyNode(
            formattedView,
            selectedIds,
            "unassessed"
        );

    private buildNodeTypeToNodeExtractors = (
        riskDirectory: R.RiskDirectoryView,
        savedBlobs: SavedBlob[]
    ): Record<
        "Process" | "Risk",
        DV.DataExtractor<T.NodeWithMD, T.MetadataInstance>[]
    > => {
        const { responseEnumMap } = this.props;
        const formulaComputer = new FormulaComputer();
        const nodeTypeToExtractors: Record<
            R.NodeTypeWithMetadata,
            DV.DataExtractor<T.NodeWithMD, T.MetadataInstance>[]
        > = {
            Process: [],
            Risk: [],
        };
        const keys: ["Process", "Risk"] = ["Process", "Risk"];
        for (const nodeType of keys) {
            let fields = U.getRiskDirectoryLevelMDFields(
                riskDirectory,
                nodeType
            );
            if (nodeType === "Risk") {
                fields = fields.concat(U.getEntityLevelMDFields(riskDirectory));
            }
            nodeTypeToExtractors[nodeType] = DV.buildDataExtractors(
                fields.map((field) =>
                    U.buildNodeMetadataExtractorSpec(
                        field,
                        riskDirectory,
                        savedBlobs,
                        responseEnumMap,
                        formulaComputer
                    )
                )
            );
        }
        return nodeTypeToExtractors;
    };

    private buildControlsExtractor = (): DV.DataExtractor<
        DV.NodeDescriptor<T.MapNode>,
        MitigatingControl[],
        IdAndName<ControlId, string>
    > => {
        const stringCompare = Cmp.string();
        const controlIdToName = this.controlIdToName("map");
        return {
            id: RISK_EXTRACTOR_KEYS.CONTROLS,
            label: t("RiskMapResponse_Columns_Controls"),
            getValue: this.getMitigatingControls,
            asString: this.renderControls,
            compare: Cmp.map(this.renderControls, stringCompare),
            customFilterTransformer: {
                getValue: (_node) => {
                    throw new Error(
                        `Unexpected call of customFilterTransformer.getValue() on ${RISK_EXTRACTOR_KEYS.CONTROLS} extractor`
                    );
                },
                asString: ({ id, name }) => controlIdToName[id] || name,
            },
        };
    };

    private buildControlProgramExtractor = (): DV.DataExtractor<
        DV.NodeDescriptor<T.MapNode>,
        {
            controlProgramIds: number[];
            mitigatingControls: MitigatingControl[];
        },
        IdAndName<ControlProgramId, string>
    > => {
        const stringCompare = Cmp.string();
        const controlProgramIdToName = this.controlProgramIdToName("map");
        return {
            id: RISK_EXTRACTOR_KEYS.CONTROL_PROGRAMS,
            label: t("ControlPrograms"),
            getValue: this.getDedupMitigatingControlProgramsIds,
            asString: this.renderControlPrograms,
            compare: Cmp.map(this.renderControlPrograms, stringCompare),
            customFilterTransformer: {
                getValue: (_node) => {
                    throw new Error(
                        `Unexpected call of customFilterTransformer.getValue() on ${RISK_EXTRACTOR_KEYS.CONTROL_PROGRAMS} extractor`
                    );
                },
                asString: ({ id, name }) => controlProgramIdToName[id] || name,
            },
        };
    };

    private buildEntityExtractor = (): DV.DataExtractor<
        DV.NodeDescriptor<T.MapNode>,
        EntityId | undefined,
        IdAndName<EntityId, string> | undefined
    > => {
        const stringCompare = Cmp.string();
        const entityIdToName = this.entityIdToName();
        return {
            id: RISK_EXTRACTOR_KEYS.EXPANDED_RISK_NODE_ENTITY,
            label: t("RiskMaps_Edit_AssignedEntities"),
            getValue: this.getExpandedRiskNodeEntity,
            asString: this.renderExpandedRiskNodeEntity,
            compare: Cmp.map(this.renderExpandedRiskNodeEntity, stringCompare),
            customFilterTransformer: {
                getValue: (desc) => {
                    const id = this.getExpandedRiskNodeEntity(desc);
                    return id !== undefined
                        ? { id, name: entityIdToName[id] }
                        : undefined;
                },
                asString: (idAndName) =>
                    idAndName !== undefined
                        ? entityIdToName[idAndName.id] || idAndName.name
                        : "",
            },
        };
    };

    private buildRiskExtractor = (
        nodeIdToName: Record<RiskNodeId, string>
    ): DV.DataExtractor<
        DV.NodeDescriptor<T.MapNode>,
        string | undefined,
        IdAndName<RiskNodeId, string> | undefined
    > => {
        const { common } = this.props;
        const stringCompare = Cmp.string();
        return {
            id: RISK_EXTRACTOR_KEYS.RISK,
            label: t("RiskMaps_Edit_EditTree_NodeMenu_Risk"),
            getValue: unwrapDescriptor(common.getRisk),
            asString: unwrapDescriptor((node) => common.getRisk(node) || ""),
            compare: Cmp.map(
                unwrapDescriptor(common.getRisk),
                Cmp.undefinedLast(stringCompare)
            ),
            customFilterTransformer: {
                getValue: unwrapDescriptor((node) => {
                    const name = common.getRisk(node);
                    if (name !== undefined) {
                        return { id: U.getNodeGuid(node), name };
                    } else {
                        return undefined;
                    }
                }),
                asString: (idAndName) =>
                    idAndName !== undefined
                        ? nodeIdToName[idAndName.id] || idAndName.name
                        : "",
            },
        };
    };

    private buildProcessesExtractor = (
        nodeIdToName: Record<RiskNodeId, string>
    ): DV.DataExtractor<
        DV.NodeDescriptor<T.MapNode>,
        ProcessNode[],
        IdAndName<RiskNodeId, string>
    > => {
        const { common } = this.props;
        const stringCompare = Cmp.string();
        return {
            id: RISK_EXTRACTOR_KEYS.PROCESSES,
            label: t("RiskMaps_Edit_EditTree_NodeMenu_Process"),
            getValue: common.getProcesses,
            asString: (desc) =>
                common
                    .getProcesses(desc)
                    .map((process) => process.content.name)
                    .join(),
            compare: Cmp.map(
                (desc) =>
                    common
                        .getProcesses(desc)
                        .map((process) => process.content.name),
                (p1, p2) => Cmp.arrayCompare(p1, p2, stringCompare)
            ),
            customFilterTransformer: {
                getValue: (_node) => {
                    throw new Error(
                        `Unexpected call of customFilterTransformer.getValue() on ${RISK_EXTRACTOR_KEYS.PROCESSES} extractor`
                    );
                },
                asString: ({ id, name }) => nodeIdToName[id] || name,
            },
        };
    };

    private buildMapDataView = (
        key: CacheKey,
        riskDirectory: R.RiskDirectoryView,
        rootNode: RiskDirectoryNodeDto,
        entityList: EntityOverviewListDto,
        common: T.CommonGetters
    ): DV.DataView<DV.NodeDescriptor<T.MapNode>> => {
        const entities = common.entities(entityList, rootNode);
        const nodeTypeToNodeExtractors = this.nodeTypeToNodeExtractors(
            key,
            riskDirectory,
            rootNode.linkedResources.blobs
        );
        // These extractors can work both on DirectorNode and MapNode, so they can also work on just MapNode
        const colsByLevel = common.metadataColumns(riskDirectory);
        const metadataExtractors = U.buildColumnMetadataExtractors(
            colsByLevel.riskDirectoryLevel
                .concat(colsByLevel.entityLevel)
                .filter((col) => col.fields.Risk !== undefined),
            nodeTypeToNodeExtractors
        ) as DV.DataExtractor<
            DV.NodeDescriptor<T.MapNode>,
            T.MetadataInstance,
            IdAndName<MetadataColumnId, string>
        >[];

        // These extractors are for filtering purposes only, preventing unwanted filtering results with processes
        const filterMetadataExtractors = U.buildFilterMetadataExtractors(
            colsByLevel,
            nodeTypeToNodeExtractors,
            true
        ) as DV.DataExtractor<
            DV.NodeDescriptor<T.MapNode>,
            T.MetadataInstance,
            IdAndName<MetadataColumnId, string>
        >[];

        const dataSource: DV.DataSource<DV.NodeDescriptor<T.MapNode>> =
            new DV.MultiTreeDataSource<T.MapNode>(
                [rootNode.content.resource.rootNode],
                (node) => this.getMapChildren(node, common),
                U.getId,
                U.forwardToRisk((node) => node.content.name)
            );
        const stringCompare = Cmp.string();
        const defaultCompareItems: Cmp.ComparisonFunction<
            DV.NodeDescriptor<T.MapNode>
        > = Cmp.chain([
            Cmp.map((d) => d.index),
            Cmp.map((d) => {
                if (d.value.type === "ExpandedRiskNode") {
                    return entities[d.value.entityId].name;
                } else {
                    return d.value.content.name; // Should never occur
                }
            }, stringCompare),
        ]);

        const {
            content: {
                resource: {
                    structure: { blessedFieldIds },
                },
            },
        } = riskDirectory;

        const inherentImpactExtractor = nodeTypeToNodeExtractors.Risk.find(
            (ext) => ext.id === blessedFieldIds.inherentImpactId
        )!;
        const inherentLikelihoodExtractor = nodeTypeToNodeExtractors.Risk.find(
            (ext) => ext.id === blessedFieldIds.inherentLikelihoodId
        )!;
        const residualImpactExtractor = nodeTypeToNodeExtractors.Risk.find(
            (ext) => ext.id === blessedFieldIds.residualImpactId
        )!;
        const residualLikelihoodExtractor = nodeTypeToNodeExtractors.Risk.find(
            (ext) => ext.id === blessedFieldIds.residualLikelihoodId
        )!;

        function buildNodeScopeExtractor(): DV.DataExtractor<
            DV.NodeDescriptor<T.MapNode>,
            T.NodeScope,
            BinaryNodeScope | undefined
        > {
            return {
                id: RISK_EXTRACTOR_KEYS.FILTER_SCOPE,
                label: t("RiskDirectory_Scope"),
                getValue: unwrapDescriptor(U.getFilterScope),
                asString: unwrapDescriptor((node) =>
                    U.localizeFilterScope(U.getFilterScope(node)?.scope)
                ),
                compare: Cmp.map(
                    unwrapDescriptor((node) =>
                        U.localizeFilterScope(U.getFilterScope(node)?.scope)
                    ),
                    stringCompare
                ),
                customFilterTransformer: {
                    getValue: unwrapDescriptor(
                        (node) => U.getFilterScope(node)?.scope
                    ),
                    asString: U.localizeFilterScope,
                },
            };
        }

        function buildAssessmentStatusExtractor(): DV.DataExtractor<
            DV.NodeDescriptor<T.MapNode>,
            U.NodeAssessmentStatus,
            BinaryAssessmentStatus | undefined
        > {
            return {
                id: RISK_EXTRACTOR_KEYS.ASSESSMENT_STATUS,
                label: t("RiskMaps_Edit_AssessmentStatus"),
                getValue: unwrapDescriptor((node) =>
                    U.getAssessmentStatus(node)
                ),
                asString: unwrapDescriptor((node) =>
                    U.localizeAssessmentStatus(U.getAssessmentStatus(node))
                ),
                compare: Cmp.map(
                    unwrapDescriptor((node) =>
                        U.localizeAssessmentStatus(U.getAssessmentStatus(node))
                    ),
                    stringCompare
                ),
                customFilterTransformer: {
                    getValue: unwrapDescriptor((node) =>
                        U.assessmentStatusFilter(U.getAssessmentStatus(node))
                    ),
                    asString: U.localizeAssessmentStatusFilter,
                },
            };
        }

        const nodeIdToName = this.nodeIdToName();

        const extractorGroups: DV.DataExtractorGroup<
            DV.NodeDescriptor<T.MapNode>
        >[] = [
            {
                id: "extractor-group",
                label: "Extractor group",
                extractors: [
                    {
                        id: RISK_EXTRACTOR_KEYS.PATH,
                        label: t("RiskMapResponse_Path"),
                        getValue: this.getMapPath,
                        asString: this.getMapPathAsString,
                        compare: Cmp.map(
                            this.getMapPathAsString,
                            stringCompare
                        ),
                    },
                    {
                        id: RISK_EXTRACTOR_KEYS.NAME,
                        label: t("RiskMaps_Edit_EditTree_NodeName"),
                        getValue: U.unwrapForwardToRisk(
                            (node) => node.content.name
                        ),
                        asString: U.unwrapForwardToRisk(
                            (node) => node.content.name
                        ),
                        compare: Cmp.map(
                            U.unwrapForwardToRisk((node) => node.content.name),
                            stringCompare
                        ),
                    },
                    {
                        id: RISK_EXTRACTOR_KEYS.DESCRIPTION,
                        label: t("RiskMaps_Edit_EditTree_NodeDescription"),
                        getValue: common.getDescription,
                        asString: common.getDescription,
                        compare: Cmp.map(common.getDescription, stringCompare),
                    },
                    ...metadataExtractors,
                    ...filterMetadataExtractors,
                    downgradeExtractorType(this.buildControlsExtractor()),
                    downgradeExtractorType(this.buildControlProgramExtractor()),
                    {
                        id: RISK_EXTRACTOR_KEYS.ENTITIES,
                        label: t("RiskMaps_Edit_AssignedEntities"),
                        getValue: common.getEntities,
                        asString: common.renderEntities,
                        compare: Cmp.map(common.renderEntities, stringCompare),
                    },
                    downgradeExtractorType(this.buildEntityExtractor()),
                    downgradeExtractorType(
                        this.buildRiskExtractor(nodeIdToName)
                    ),
                    downgradeExtractorType(
                        this.buildProcessesExtractor(nodeIdToName)
                    ),
                    {
                        id: RISK_EXTRACTOR_KEYS.SCOPE,
                        label: t("RiskDirectory_Scope"), // Used in exports, not translated.
                        getValue: unwrapDescriptor(U.getScope),
                        asString: unwrapDescriptor(
                            (node) => U.getScope(node).scope
                        ),
                        compare: Cmp.map(
                            unwrapDescriptor((node) => U.getScope(node).scope),
                            stringCompare
                        ),
                    },
                    downgradeExtractorType(buildNodeScopeExtractor()),
                    downgradeExtractorType(buildAssessmentStatusExtractor()),
                    {
                        id: RISK_EXTRACTOR_KEYS.ASSESSMENT_USER,
                        label: t("RiskMaps_Edit_AssessedBy"),
                        getValue: unwrapDescriptor((node) =>
                            U.getAssessmentUser(node)
                        ),
                        asString: unwrapDescriptor((node) =>
                            U.getAssessmentUserName(node, rootNode)
                        ),
                        compare: Cmp.map(
                            unwrapDescriptor((node) =>
                                U.getAssessmentUserName(node, rootNode)
                            ),
                            stringCompare
                        ),
                    },
                    {
                        id: RISK_EXTRACTOR_KEYS.ASSESSMENT_DATE,
                        label: "Assessed at", // Used in exports, not translated.
                        getValue: unwrapDescriptor((node) =>
                            U.getAssessmentDate(node)
                        ),
                        asString: unwrapDescriptor((node) =>
                            U.localizeAssessmentDate(node)
                        ),
                        compare: Cmp.map(
                            unwrapDescriptor((node) =>
                                U.localizeAssessmentDate(node)
                            ),
                            stringCompare
                        ),
                    },
                    {
                        id: RISK_EXTRACTOR_KEYS.INHERENT_FOCUS,
                        label: focusToString(
                            inherentImpactExtractor.label,
                            inherentLikelihoodExtractor.label
                        ),
                        getValue: unwrapDescriptor((node) =>
                            node.type === "ExpandedRiskNode"
                                ? [
                                      inherentImpactExtractor.getValue(node),
                                      inherentLikelihoodExtractor.getValue(
                                          node
                                      ),
                                  ]
                                : undefined
                        ),
                        asString: unwrapDescriptor((node) =>
                            node.type === "ExpandedRiskNode"
                                ? focusToString(
                                      inherentImpactExtractor.asString(node),
                                      inherentLikelihoodExtractor.asString(node)
                                  )
                                : ""
                        ),
                        compare: Cmp.map(
                            unwrapDescriptor((node) =>
                                node.type === "ExpandedRiskNode"
                                    ? node
                                    : undefined
                            ),
                            Cmp.chain([
                                Cmp.undefinedLast(
                                    inherentImpactExtractor.compare
                                ),
                                Cmp.undefinedLast(
                                    inherentLikelihoodExtractor.compare
                                ),
                            ])
                        ),
                    },
                    {
                        id: RISK_EXTRACTOR_KEYS.RESIDUAL_FOCUS,
                        label: focusToString(
                            residualImpactExtractor.label,
                            residualLikelihoodExtractor.label
                        ),
                        getValue: unwrapDescriptor((node) =>
                            node.type === "ExpandedRiskNode"
                                ? [
                                      residualImpactExtractor.getValue(node),
                                      residualLikelihoodExtractor.getValue(
                                          node
                                      ),
                                  ]
                                : undefined
                        ),
                        asString: unwrapDescriptor((node) =>
                            node.type === "ExpandedRiskNode"
                                ? focusToString(
                                      residualImpactExtractor.asString(node),
                                      residualLikelihoodExtractor.asString(node)
                                  )
                                : ""
                        ),
                        compare: Cmp.map(
                            unwrapDescriptor((node) =>
                                node.type === "ExpandedRiskNode"
                                    ? node
                                    : undefined
                            ),
                            Cmp.chain([
                                Cmp.undefinedLast(
                                    residualImpactExtractor.compare
                                ),
                                Cmp.undefinedLast(
                                    residualLikelihoodExtractor.compare
                                ),
                            ])
                        ),
                    },
                ],
            },
        ];
        const sorters: DV.Sorter[] = [];

        return {
            dataSource,
            defaultCompareItems,
            extractorGroups,
            sorters,
        };
    };
    private buildMapColumnBuilders = (
        riskDirectory: R.RiskDirectoryView
    ): Record<
        string,
        | ColumnBuilder<DV.NodeDescriptor<T.MapNode>, string>
        | Record<ModeType, ColumnBuilder<DV.NodeDescriptor<T.MapNode>, string>>
    > => {
        const { common } = this.props;
        const metadataColumns: Record<
            string,
            ColumnBuilder<DV.NodeDescriptor<T.MapNode>, string>
        > = {};
        const riskDirectoryMetadataColumns =
            common.metadataColumns(riskDirectory);
        const riskMetadataColumns = [
            ...riskDirectoryMetadataColumns.riskDirectoryLevel,
            ...riskDirectoryMetadataColumns.entityLevel,
        ].filter((col) => col.fields.Risk !== undefined);

        const {
            content: {
                resource: {
                    structure: { blessedFieldIds },
                },
            },
        } = riskDirectory;

        const impactLikelihoodColumns = [
            blessedFieldIds.inherentImpactId,
            blessedFieldIds.inherentLikelihoodId,
            blessedFieldIds.residualImpactId,
            blessedFieldIds.residualLikelihoodId,
        ];

        riskMetadataColumns
            .map((col) => {
                if (
                    col.fields.Risk?.id &&
                    impactLikelihoodColumns.includes(col.fields.Risk?.id)
                ) {
                    const fieldType = col.fields.Risk?.fieldType;
                    if (
                        fieldType?.type !== "Dropdown" &&
                        fieldType?.type !== "RadioButtonGroup"
                    ) {
                        throw new Error(
                            `Invalid field type, got ${fieldType?.type}, Dropdown or RadioButtonGroup expected`
                        );
                    }
                    const colors = colorizeMetadata(fieldType.options);
                    return {
                        id: col.id,
                        builder: (
                            extractor: DV.DataExtractor<
                                DV.NodeDescriptor<T.MapNode>,
                                string
                            >
                        ): ColumnOptions<DV.NodeDescriptor<T.MapNode>> => ({
                            display: "DEFAULT_VISIBLE",
                            getFormattedValue: (
                                desc: DV.NodeDescriptor<T.MapNode>
                            ) =>
                                desc.value.type === "ExpandedRiskNode" ? (
                                    <MetadataCircle
                                        colors={colors}
                                        label={extractor.asString(desc)}
                                    />
                                ) : (
                                    extractor.asString(desc)
                                ),
                        }),
                    };
                } else {
                    return {
                        id: col.id,
                        builder: (): ColumnOptions<
                            DV.NodeDescriptor<T.MapNode>
                        > => ({
                            display: "DEFAULT_HIDDEN",
                        }),
                    };
                }
            })
            .forEach(({ id, builder }) => (metadataColumns[id] = builder));

        /// These columns are for filter purposes only, so we never display them.
        const filterMetadataColumns: Record<
            string,
            ColumnBuilder<DV.NodeDescriptor<T.MapNode>, string>
        > = {};
        riskDirectoryMetadataColumns.riskDirectoryLevel
            .filter((col) => col.fields.Process !== undefined)
            .forEach((col) => {
                filterMetadataColumns[
                    U.prefixFilterMetadataExtractor(col.id, "Process")
                ] = (): ColumnOptions<DV.NodeDescriptor<T.MapNode>> => ({
                    display: "NEVER",
                });
            });
        riskDirectoryMetadataColumns.riskDirectoryLevel
            .filter((col) => col.fields.Risk !== undefined)
            .forEach((col) => {
                filterMetadataColumns[
                    U.prefixFilterMetadataExtractor(col.id, "Risk")
                ] = (): ColumnOptions<DV.NodeDescriptor<T.MapNode>> => ({
                    display: "NEVER",
                });
            });
        riskDirectoryMetadataColumns.entityLevel
            .filter((col) => col.fields.Risk !== undefined)
            .forEach((col) => {
                filterMetadataColumns[
                    U.prefixFilterMetadataExtractor(col.id, "Risk")
                ] = (): ColumnOptions<DV.NodeDescriptor<T.MapNode>> => ({
                    display: "NEVER",
                });
            });

        return {
            path: {
                [Mode.TABLE]: (): ColumnOptions<
                    DV.NodeDescriptor<T.MapNode>
                > => ({
                    display: "DEFAULT_VISIBLE",
                }),
                [Mode.TREE]: (): ColumnOptions<
                    DV.NodeDescriptor<T.MapNode>
                > => ({
                    display: "NEVER",
                }),
            },
            name: {
                [Mode.TABLE]: (
                    extractor
                ): ColumnOptions<DV.NodeDescriptor<T.MapNode>> => ({
                    display: "ALWAYS",
                    getFormattedValue: (desc) => (
                        <NameColumn node={desc.value}>
                            {extractor.getValue(desc)}
                        </NameColumn>
                    ),
                }),
                [Mode.TREE]: (
                    extractor
                ): ColumnOptions<DV.NodeDescriptor<T.MapNode>> => ({
                    display: "ALWAYS",
                    getFormattedValue: (desc) => (
                        <NameColumn node={desc.value}>
                            {desc.value.type === "ExpandedRiskNode" ? (
                                extractor.getValue(desc)
                            ) : (
                                <b>{extractor.getValue(desc)}</b>
                            )}
                        </NameColumn>
                    ),
                }),
            },
            description: (
                extractor
            ): ColumnOptions<DV.NodeDescriptor<T.MapNode>> => ({
                display: "DEFAULT_VISIBLE",
                getFormattedValue: (desc) =>
                    renderMarkdown(extractor.getValue(desc) || ""),
            }),
            entities: (): ColumnOptions<DV.NodeDescriptor<T.MapNode>> => ({
                display: "DEFAULT_VISIBLE",
            }),
            expandedRiskNodeEntity: (): ColumnOptions<
                DV.NodeDescriptor<T.MapNode>
            > => ({ display: "NEVER" }),
            risk: (): ColumnOptions<DV.NodeDescriptor<T.MapNode>> => ({
                display: "NEVER",
            }),
            processes: (): ColumnOptions<DV.NodeDescriptor<T.MapNode>> => ({
                display: "NEVER",
            }),
            controls: (): ColumnOptions<DV.NodeDescriptor<T.MapNode>> => ({
                display: "DEFAULT_VISIBLE",
            }),
            controlPrograms: (): ColumnOptions<
                DV.NodeDescriptor<T.MapNode>
            > => ({
                display: "NEVER",
            }),
            scope: (): ColumnOptions<DV.NodeDescriptor<T.MapNode>> => ({
                display: "NEVER",
            }),
            filterScope: (): ColumnOptions<DV.NodeDescriptor<T.MapNode>> => ({
                display: "NEVER",
            }),
            inherentFocus: (): ColumnOptions<DV.NodeDescriptor<T.MapNode>> => ({
                display: "NEVER",
            }),
            residualFocus: (): ColumnOptions<DV.NodeDescriptor<T.MapNode>> => ({
                display: "NEVER",
            }),
            assessmentDate: (): ColumnOptions<
                DV.NodeDescriptor<T.MapNode>
            > => ({
                display: "NEVER",
            }),
            assessmentUser: (): ColumnOptions<
                DV.NodeDescriptor<T.MapNode>
            > => ({
                display: "NEVER",
            }),
            assessmentStatus: (
                extractor
            ): ColumnOptions<DV.NodeDescriptor<T.MapNode>> => ({
                display: "DEFAULT_VISIBLE",
                getFormattedValue: (desc) => (
                    <AssessmentStatusColumn
                        status={
                            extractor.getValue(desc) as AssessmentStatus["type"]
                        }
                    />
                ),
            }),
            ...metadataColumns,
            ...filterMetadataColumns,
        };
    };

    private controlIdToName = (key: CacheKey): Record<ControlId, string> => {
        const mapIdToName: Record<ControlId, string> = {};
        const controls = this.controlMap(key, this.props.rootNode);
        for (const [id, control] of Object.entries(controls)) {
            mapIdToName[id] = control.title;
        }
        return mapIdToName;
    };

    private controlProgramIdToName = (
        key: CacheKey
    ): Record<ControlProgramId, string> => {
        const controlPrograms = this.controlProgramMap(
            key,
            this.props.rootNode
        );
        const mapIdToName: Record<ControlProgramId, string> = {};
        for (const id in controlPrograms) {
            if (Object.prototype.hasOwnProperty.call(controlPrograms, id)) {
                mapIdToName[id] = controlPrograms[id].name;
            }
        }
        return mapIdToName;
    };

    private entityIdToName = (): Record<EntityId, string> => {
        const mapIdToName: Record<EntityId, string> = {};
        const { entityList, common, rootNode } = this.props;
        const entities = common.entities(entityList, rootNode);
        for (const id in entities) {
            if (Object.prototype.hasOwnProperty.call(entities, id)) {
                mapIdToName[id] = entities[id].name;
            }
        }
        return mapIdToName;
    };

    private nodeIdToName = (): Record<RiskNodeId, string> => {
        const { rootNode } = this.props;

        // It's faster to process the risk directory datasource than the map datasource,
        // because we don't have to handle the expanded nodes.
        const notExpandedDataSource: DV.DataSource<
            DV.NodeDescriptor<T.DirectoryNode>
        > = new DV.MultiTreeDataSource<T.DirectoryNode>(
            [rootNode.content.resource.rootNode],
            U.getDirectoryChildren,
            U.getId,
            U.forwardToRisk((node) => node.content.name)
        );

        const nodeIdToName: Record<RiskNodeId, string> = {};
        DV.forEach(notExpandedDataSource.iter(), (desc) => {
            const node = desc.value;
            nodeIdToName[U.getNodeGuid(node)] = node.content.name;
        });

        return nodeIdToName;
    };

    private buildMapDataViewIndex = (
        key: CacheKey,
        riskDirectory: R.RiskDirectoryView,
        rootNode: RiskDirectoryNodeDto,
        entityList: EntityOverviewListDto,
        common: T.CommonGetters
    ): AsyncDataViewIndex<DV.NodeDescriptor<T.MapNode>> => {
        const dataView = this.mapDataView(
            key,
            riskDirectory,
            rootNode,
            entityList,
            common
        );

        const { expanders, extraExtractors } = this.buildMapExtractorExpanders(
            key,
            riskDirectory
        );

        return new AsyncDataViewIndex(
            dataView,
            [
                RISK_EXTRACTOR_KEYS.RISK,
                RISK_EXTRACTOR_KEYS.PROCESSES,
                ...extraExtractors,
                RISK_EXTRACTOR_KEYS.EXPANDED_RISK_NODE_ENTITY,
                RISK_EXTRACTOR_KEYS.CONTROLS,
                RISK_EXTRACTOR_KEYS.CONTROL_PROGRAMS,
                RISK_EXTRACTOR_KEYS.FILTER_SCOPE,
                RISK_EXTRACTOR_KEYS.ASSESSMENT_STATUS,
            ],
            expanders
        );
    };
    private buildMapExtractorExpanders = (
        key: CacheKey,
        riskDirectory: R.RiskDirectoryView
    ): {
        expanders: Array<DV.ExtractorExpander<any, any>>;
        extraExtractors: Array<string>;
    } => {
        const { common } = this.props;
        type Expander = DV.ExtractorExpander<
            T.MetadataInstance,
            T.DataViewIndexValue,
            T.MetadataValueForFilter
        >[];

        const entityLevelMetadataColumns = common
            .metadataColumns(riskDirectory)
            .entityLevel.filter(
                (col) =>
                    col.fields.Risk !== undefined &&
                    col.fields.Risk.fieldType.type !== "TextArea"
            );
        const stringCompare = Cmp.string();
        const expand = (
            acc: Expander,
            f: R.MetadataField,
            id: string
        ): void => {
            switch (f.fieldType.type) {
                case "CheckboxGroup":
                    acc.push({
                        extractorId: id,
                        expand: (instance: T.MetadataInstance) => {
                            if (
                                instance !== undefined &&
                                instance.type !== "CheckboxGroupInstance"
                            ) {
                                throw new Error(
                                    "Expected CheckboxGroupInstance for field of type CheckboxGroup, got " +
                                        instance.type
                                );
                            }
                            return (
                                instance?.value?.map((opt) => ({
                                    partialId: opt.id,
                                    expandedValue: {
                                        type: "OptionValue",
                                        value: opt,
                                    },
                                })) ?? []
                            );
                        },
                        compare: Cmp.map(
                            ({ value }) => (value as R.OptionValue).value,
                            stringCompare
                        ), // Casting is safe because an error is thrown before if the type is wrong
                        asString: ({ value }) => (value as R.OptionValue).value, // Casting is safe, see above
                        customFilterTransformer: {
                            getValue: (dviValue) => {
                                if (dviValue.type !== "OptionValue") {
                                    throw new Error(
                                        "Expected OptionValue for field of type CheckboxGroup, got " +
                                            dviValue.type
                                    );
                                }
                                return {
                                    id: dviValue.value.id,
                                    name: dviValue.value.value,
                                };
                            },
                            asString: U.stringifyMetadataValueForFilter,
                        },
                    });
                    break;
                case "AttachedFiles":
                    acc.push({
                        extractorId: id,
                        expand: (instance: T.MetadataInstance) => {
                            if (
                                instance !== undefined &&
                                instance.type !== "AttachedFilesInstance"
                            ) {
                                throw new Error(
                                    "Expected AttachedFilesInstance for field of type AttachedFiles, got " +
                                        instance.type
                                );
                            }
                            return (
                                instance?.value?.map((blob) => ({
                                    partialId: blob.id,
                                    expandedValue: {
                                        type: "AttachedFile",
                                        value: blob,
                                    },
                                })) ?? []
                            );
                        },
                        compare: Cmp.map(
                            ({ value }) => (value as SavedBlob).fileName,
                            stringCompare
                        ), // Casting is safe because an error is thrown before if the type is wrong
                        asString: ({ value }) => (value as SavedBlob).fileName, // Casting is safe, see above
                        customFilterTransformer: {
                            getValue: (dviValue) => {
                                if (dviValue.type !== "AttachedFile") {
                                    throw new Error(
                                        "Expected AttachedFile for field of type AttachedFile, got " +
                                            dviValue.type
                                    );
                                }
                                return {
                                    id: dviValue.value.id,
                                    name: dviValue.value.fileName,
                                };
                            },
                            asString: U.stringifyMetadataValueForFilter,
                        },
                    });
                    break;
                case "TextInput":
                case "Heading":
                case "TextArea":
                case "NumericInput":
                case "Dropdown":
                case "RadioButtonGroup":
                    break;
                default:
                    assertNever(f.fieldType);
            }
        };

        const newColIds: string[] = [];
        const entityLevelExpanders = entityLevelMetadataColumns.reduce(
            (acc: Expander, col): Expander => {
                if (col.fields.Risk !== undefined) {
                    const id = U.prefixFilterMetadataExtractor(col.id, "Risk");
                    expand(acc, col.fields.Risk, id);
                    newColIds.push(id);
                }
                return acc;
            },
            []
        );

        const rdLevelExpanders = common
            .metadataColumns(riskDirectory)
            .riskDirectoryLevel.reduce((acc: Expander, col): Expander => {
                if (
                    col.fields.Process !== undefined &&
                    col.fields.Process.fieldType.type !== "TextArea"
                ) {
                    const id = U.prefixFilterMetadataExtractor(
                        col.id,
                        "Process"
                    );
                    expand(acc, col.fields.Process, id);
                    newColIds.push(id);
                }
                if (
                    col.fields.Risk !== undefined &&
                    col.fields.Risk.fieldType.type !== "TextArea"
                ) {
                    const id = U.prefixFilterMetadataExtractor(col.id, "Risk");
                    expand(acc, col.fields.Risk, id);
                    newColIds.push(id);
                }
                return acc;
            }, []);

        const controlIdToName = this.controlIdToName(key);
        const controlProgramIdToName = this.controlProgramIdToName(key);
        const nodeIdToName = this.nodeIdToName();

        const expanders: Array<DV.ExtractorExpander<any, any>> = [
            {
                extractorId: RISK_EXTRACTOR_KEYS.CONTROLS,
                expand: (values: MitigatingControl[] | undefined) =>
                    (values || []).flatMap(({ controlId, historicControl }) => {
                        const expanded = [];

                        if (controlId in controlIdToName) {
                            expanded.push({
                                partialId: `control-${controlId}`,
                                expandedValue: {
                                    id: controlId,
                                    value: controlIdToName[controlId],
                                },
                            });
                        }
                        if (historicControl) {
                            expanded.push({
                                partialId: `control-${controlId}-historic`,
                                expandedValue: {
                                    id: controlId,
                                    value: historicControl.title,
                                },
                            });
                        }

                        return expanded;
                    }),
                compare: Cmp.map(({ value }) => value, stringCompare),
                asString: (_expandedValue) => {
                    throw new Error(
                        `Unexpected call of asString() on ${RISK_EXTRACTOR_KEYS.CONTROLS} expander`
                    );
                },
                customFilterTransformer: {
                    getValue: ({ id, value }) => ({ id, name: value }),
                    asString: ({ id, name }) => controlIdToName[id] || name,
                },
            },
            {
                extractorId: RISK_EXTRACTOR_KEYS.CONTROL_PROGRAMS,
                expand: (
                    values:
                        | {
                              controlProgramIds: number[];
                              mitigatingControls: MitigatingControl[];
                          }
                        | undefined
                ) => {
                    const expanded = [];

                    for (const id of values?.controlProgramIds ?? []) {
                        if (id in controlProgramIdToName) {
                            expanded.push({
                                partialId: `controlProgram-${id}`,
                                expandedValue: {
                                    id,
                                    value: controlProgramIdToName[id],
                                },
                            });
                        }
                    }

                    for (const {
                        historicControl,
                    } of values?.mitigatingControls ?? []) {
                        if (historicControl) {
                            expanded.push({
                                partialId: `controlProgram-${historicControl.controlProgramId}-historic`,
                                expandedValue: {
                                    id: historicControl.controlProgramId,
                                    value: historicControl.controlProgramName,
                                },
                            });
                        }
                    }

                    return expanded;
                },
                compare: Cmp.map(({ value }) => value, stringCompare),
                asString: (_expandedValue) => {
                    throw new Error(
                        `Unexpected call of asString() on ${RISK_EXTRACTOR_KEYS.CONTROL_PROGRAMS} expander`
                    );
                },
                customFilterTransformer: {
                    getValue: ({ id, value }) => ({ id, name: value }),
                    asString: ({ id, name }) =>
                        controlProgramIdToName[id] || name,
                },
            },
            {
                extractorId: RISK_EXTRACTOR_KEYS.PROCESSES,
                expand: (values: R.ProcessNode[]) =>
                    values.map((process) => ({
                        partialId: `process-${process.id}`,
                        expandedValue: {
                            id: process.id,
                            value: process.content.name,
                        },
                    })),
                compare: Cmp.map(({ value }) => value, stringCompare),
                asString: (_expandedValue) => {
                    throw new Error(
                        `Unexpected call of asString() on ${RISK_EXTRACTOR_KEYS.PROCESSES} expander`
                    );
                },
                customFilterTransformer: {
                    getValue: ({ id, value }) => ({ id, name: value }),
                    asString: ({ id, name }) => nodeIdToName[id] || name,
                },
            },
            ...rdLevelExpanders,
            ...entityLevelExpanders,
        ];

        return {
            expanders,
            extraExtractors: newColIds,
        };
    };
    private buildMapQueryResult = (
        key: CacheKey,
        riskDirectory: R.RiskDirectoryView,
        rootNode: RiskDirectoryNodeDto,
        filter: SimpleAndFilter | null,
        entityList: EntityOverviewListDto,
        common: T.CommonGetters = this.props.common
    ): Promise<QueryResult<DV.NodeDescriptor<T.MapNode>>> => {
        const dataViewIndex = this.mapDataViewIndex(
            key,
            riskDirectory,
            entityList,
            common
        )(rootNode);
        return dataViewIndex.find(filter);
    };
    private buildFocusQueryResult = (
        key: CacheKey,
        riskDirectory: R.RiskDirectoryView,
        rootNode: RiskDirectoryNodeDto,
        mapFilter: SimpleAndFilter | null,
        coords: CellCoord[] | null,
        entityList: EntityOverviewListDto,
        mapMode: MapMode,
        common: T.CommonGetters = this.props.common
    ): Promise<QueryResult<DV.NodeDescriptor<T.MapNode>>> => {
        const filter = this.focusFilter(key, riskDirectory, mapMode, coords);
        const dataViewIndex = this.focusDataViewIndex(
            key,
            riskDirectory,
            mapFilter,
            entityList,
            common
        )(rootNode);
        return dataViewIndex.find(filter);
    };
    private buildFocusFilter = (
        riskDirectory: R.RiskDirectoryView,
        mapMode: MapMode,
        coords: CellCoord[]
    ): SimpleAndFilterWithContext<CellCoord> => {
        const { xAxis, yAxis } = this.getMode(riskDirectory, mapMode);
        let extractorId: string;
        switch (mapMode) {
            case INHERENT_MODE:
                extractorId = "inherentFocus";
                break;
            case RESIDUAL_MODE:
                extractorId = "residualFocus";
                break;
            default:
                assertNever(mapMode);
        }

        return {
            filters: [
                {
                    extractorId,
                    filters: coords.map((coord) => ({
                        value: focusToString(
                            yAxis.values[coord.y],
                            xAxis.values[coord.x]
                        ),
                        matchType: MATCH_TYPE_EXACT_IGNORE_CASE,
                        ...coord,
                    })),
                },
            ],
        };
    };

    private getMapPath = (desc: DV.NodeDescriptor<T.MapNode>): string[] => {
        if (desc.ancestors.length === 0) {
            return [];
        }
        const lastAncestorId = desc.ancestors[desc.ancestors.length - 1].id;
        if (!(lastAncestorId in this.mapPathCache)) {
            for (let i = 0; i < desc.ancestors.length; i++) {
                const ancestor = desc.ancestors[i];
                if (!(ancestor.id in this.mapPathCache)) {
                    if (i === 0) {
                        this.mapPathCache[ancestor.id] = [
                            this.computeNameInMapPath(ancestor),
                        ];
                    } else {
                        const previousAncestorId = desc.ancestors[i - 1].id;
                        const newCacheEntry =
                            this.mapPathCache[previousAncestorId].slice();
                        newCacheEntry.push(this.computeNameInMapPath(ancestor));
                        this.mapPathCache[ancestor.id] = newCacheEntry;
                    }
                }
            }
        }
        return this.mapPathCache[lastAncestorId];
    };
    private getMapPathAsString = (desc: DV.NodeDescriptor<T.MapNode>) => {
        const mapPath = this.getMapPath(desc);
        return mapPath.join(PATH_SEPARATOR);
    };
    private computeNameInMapPath = (ancestor: T.MapNode) => {
        switch (ancestor.type) {
            case "RiskDirectory":
            case "Process":
                return ancestor.content.name.indexOf(" ") < 0
                    ? ancestor.content.name
                    : ancestor.content.name.replace(/ /g, "\xA0");
            case "ExpandedRiskNode": // Should never occur because ExpandedRiskNode are always leaves
                return ancestor.risk.content.name.indexOf(" ") < 0
                    ? ancestor.risk.content.name
                    : ancestor.risk.content.name.replace(/ /g, "\xA0");
            default:
                return assertNever(ancestor);
        }
    };
    private getMitigatingControls = (
        desc: DV.NodeDescriptor<T.MapNode>
    ): MitigatingControl[] => {
        const { value: node } = desc;
        if (node.type !== "ExpandedRiskNode" || node.content === undefined) {
            return [];
        }
        return node.content.mitigatingControls;
    };
    private getDedupMitigatingControlProgramsIds = (
        node: DV.NodeDescriptor<T.MapNode>
    ): {
        controlProgramIds: number[];
        mitigatingControls: MitigatingControl[];
    } => {
        const {
            rootNode: {
                linkedResources: { controls },
            },
        } = this.props;
        const mitigatingControls = this.getMitigatingControls(node);
        return {
            mitigatingControls,
            controlProgramIds: mitigatingControls
                .map(({ controlId, historicControl }) => {
                    if (historicControl) {
                        return historicControl.controlProgramId;
                    }
                    const control = controls.find(
                        (control) => control.id === controlId
                    );
                    if (control === undefined) {
                        throw new Error(
                            `Could not find control id ${controlId}`
                        );
                    }
                    return control.controlProgramId;
                })
                .sort(Cmp.primitive)
                .filter((id, i, arr) => i === 0 || id !== arr[i - 1]),
        };
    };
    private renderControls = (node: DV.NodeDescriptor<T.MapNode>) => {
        const { rootNode } = this.props;
        const controlMap = this.controlMap("map", rootNode);
        const mitigatingControls = this.getMitigatingControls(node);
        if (mitigatingControls.length > 5) {
            return t("RiskDirectory_View_ControlCount", {
                count: mitigatingControls.length,
            });
        } else {
            return mitigatingControls
                .map(
                    ({ controlId, historicControl }) =>
                        historicControl?.title ?? controlMap[controlId].title
                )
                .join(", ");
        }
    };
    private renderControlPrograms = (node: DV.NodeDescriptor<T.MapNode>) => {
        const { rootNode } = this.props;
        const controlMap = this.controlMap("map", rootNode);
        const controlProgramMap = this.controlProgramMap("map", rootNode);
        const mitigatingControlProgramIds = this.getMitigatingControls(node)
            .map(({ controlId, historicControl }) => ({
                id:
                    historicControl?.controlProgramId ??
                    controlMap[controlId].controlProgramId,
                historicControlProgramName: historicControl?.controlProgramName,
            }))
            .sort(Cmp.map(({ id }) => id, Cmp.primitive))
            .filter(({ id }, i, arr) => i === 0 || id !== arr[i - 1].id);
        if (mitigatingControlProgramIds.length > 5) {
            return t("RiskDirectory_View_ControlCount", {
                count: mitigatingControlProgramIds.length,
            });
        } else {
            return (
                mitigatingControlProgramIds
                    // Here we prioritize the live version because the CP are deduped
                    .map(
                        ({ id, historicControlProgramName }) =>
                            controlProgramMap[id]?.name ??
                            historicControlProgramName
                    )
                    .join(", ")
            );
        }
    };

    private getMode = (
        riskDirectoryView: R.RiskDirectoryView,
        mapMode: MapMode
    ) => {
        const {
            content: {
                resource: { structure },
            },
        } = riskDirectoryView;
        const {
            blessedFieldIds: {
                inherentImpactId,
                inherentLikelihoodId,
                residualImpactId,
                residualLikelihoodId,
            },
            entityLevelFields,
        } = structure;

        const field = (id: Guid) => {
            const field = entityLevelFields.find((f) => f.id === id);
            if (field === undefined) {
                throw new Error(`Could not find column ${id}`);
            }
            switch (field.fieldType.type) {
                case "Dropdown":
                case "RadioButtonGroup":
                    return {
                        name: field.name,
                        values: field.fieldType.options.map(
                            (option) => option.value
                        ),
                    };
                case "NumericInput":
                case "TextArea":
                case "Heading":
                case "TextInput":
                case "CheckboxGroup":
                case "AttachedFiles":
                    throw new Error(
                        `Dropdown or RadioButtonGroup field expected, got ${field.fieldType.type} in column ${id}`
                    );
                default:
                    assertNever(field.fieldType);
            }
        };

        switch (mapMode) {
            case INHERENT_MODE: {
                const { name: xName, values: xValues } =
                    field(inherentLikelihoodId);
                const { name: yName, values: yValues } =
                    field(inherentImpactId);
                return {
                    xAxis: {
                        values: xValues,
                        caption: xName,
                    },
                    xToEnum: (x: string) => {
                        return xValues.indexOf(x);
                    },
                    yAxis: {
                        values: yValues,
                        caption: yName,
                    },
                    yToEnum: (y: string) => {
                        return yValues.indexOf(y);
                    },
                };
            }
            case RESIDUAL_MODE: {
                const { name: xName, values: xValues } =
                    field(residualLikelihoodId);
                const { name: yName, values: yValues } =
                    field(residualImpactId);
                return {
                    xAxis: {
                        values: xValues,
                        caption: xName,
                    },
                    xToEnum: (x: string) => {
                        return xValues.indexOf(x);
                    },
                    yAxis: {
                        values: yValues,
                        caption: yName,
                    },
                    yToEnum: (y: string) => {
                        return yValues.indexOf(y);
                    },
                };
            }
            default:
                assertNever(mapMode);
        }
    };
    private getMapChildren = (
        node: T.MapNode,
        common: T.CommonGetters
    ): T.MapNode[] => {
        const { entityList, rootNode } = this.props;
        const entities = common.entities(entityList, rootNode);
        const stringCompare = Cmp.string();
        return node.type === "ExpandedRiskNode"
            ? []
            : node.nodes.flatMap((node) => {
                  const expandedNodes = this.expandNode(node, common);
                  if (node.type === "Risk") {
                      expandedNodes.sort(
                          Cmp.map((expanded) => {
                              if (expanded.type === "ExpandedRiskNode") {
                                  return entities[expanded.entityId].name;
                              } else {
                                  return expanded.content.name; // Should never occur
                              }
                          }, stringCompare)
                      );
                  }
                  return expandedNodes;
              });
    };

    private expandRiskNode = (
        node: R.RiskNode,
        common: T.CommonGetters
    ): T.ExpandedRiskNode[] => {
        const id = (entityId: number) => `${node.id}_${entityId}`;
        const { scope } = node.content;
        switch (scope.type) {
            case "LocalWithContent":
                return [
                    {
                        type: "ExpandedRiskNode",
                        id: id(scope.entityId),
                        risk: node,
                        content: scope.content,
                        entityId: scope.entityId,
                    },
                ];
            case "GlobalWithContent":
                return common.getComputedEntityIds(node.id).map((entityId) => ({
                    type: "ExpandedRiskNode",
                    id: id(entityId),
                    risk: node,
                    content: scope.entityIdContent[entityId],
                    entityId,
                }));
        }
    };
    private expandNode = (
        node: T.DirectoryNode | T.ExpandedRiskNode,
        common: T.CommonGetters
    ): T.MapNode[] => {
        switch (node.type) {
            case "Process":
            case "RiskDirectory":
            case "ExpandedRiskNode":
                return [node];
            case "Risk":
                return this.expandRiskNode(node, common);
            default:
                assertNever(node);
        }
    };

    private handleActiveNodeChanged = (desc: DV.NodeDescriptor<T.MapNode>) => {
        this.setState({
            drawer: { nodeId: desc.value.id },
        });
    };

    private handleCloseDrawer = () => {
        this.setState({ drawer: undefined });
    };

    private getExpandedRiskNodeEntity = (
        desc: DV.NodeDescriptor<T.MapNode>
    ): number | undefined => {
        switch (desc.value.type) {
            case "ExpandedRiskNode":
                return desc.value.entityId;
            case "RiskDirectory":
            case "Process":
                return undefined;
            default:
                assertNever(desc.value);
        }
    };

    private renderExpandedRiskNodeEntity = (
        desc: DV.NodeDescriptor<T.MapNode>
    ): string => {
        const { entityList, common, rootNode } = this.props;
        const entities = common.entities(entityList, rootNode);
        const entityId = this.getExpandedRiskNodeEntity(desc);
        return entityId !== undefined ? entities[entityId].name : "";
    };
}

function unwrapDescriptor<V>(
    fn: (node: T.MapNode) => V
): (desc: DV.NodeDescriptor<T.MapNode>) => V {
    return (desc) => fn(desc.value);
}

function computeControlMap(
    _key: CacheKey,
    rootNode: RiskDirectoryNodeDto
): Record<Guid, ControlOverview> {
    return keyBy(rootNode.linkedResources.controls, (control) => control.id);
}

function computeControlProgramMap(
    _key: CacheKey,
    rootNode: RiskDirectoryNodeDto
): Record<number, ControlProgramOverview> {
    return keyBy(
        rootNode.linkedResources.controlPrograms.map((dto) => dto.resource),
        (controlProgram) => controlProgram.id
    );
}

function focusToString(impact: string, likelihood: string): string {
    return `${impact} / ${likelihood}`;
}

export type MapPageMode =
    | {
          type: "Map";
          savedDate: Moment | null;
      }
    | {
          type: "HistoricalMap";
          targetDate: Moment | null;
      }
    | {
          type: "InsightsMap";
          fromDate: Moment | null;
          toDate: Moment | null;
      };

interface MapPagePropsBase extends T.CommonProps {
    common: T.CommonGetters;
    responseEnumMap: EnumMap<R.ResponseEnum>;
    updateNode: (
        action: T.NodeUpdateAction<T.MapNode>,
        targetNode: T.MapNode
    ) => Promise<void>;
    reloadDirectory: () => void;
    mode: MapPageMode;
    filter: SimpleAndFilter | null;
    focusedEntity: T.FocusedEntity;
    updateRootNodeLinkedResources: UpdateRootNodeLinkedResourcesCallback;
    riskInsightsEnabled: boolean;
}

type MapPagePropsWithoutRiskInsights = Omit<
    MapPagePropsBase,
    "riskInsightsEnabled"
> &
    WithRouterProps &
    (
        | {
              mode: {
                  type: "Map";
                  savedDate: Moment | null;
              };
          }
        | {
              mode: {
                  type: "HistoricalMap";
                  targetDate: Moment | null;
              };
          }
        | {
              mode: {
                  type: "InsightsMap";
                  fromDate: Moment | null;
                  toDate: Moment | null;
                  diffData: DiffData;
                  diffCommon: T.CommonGetters;
                  mergedCommon: T.CommonGetters;
                  setToDate: (date: Moment | null) => void;
                  currentToDate: Moment | undefined;
              };
          }
    );

export type MapPageProps = MapPagePropsWithoutRiskInsights & {
    riskInsightsEnabled: boolean;
};
interface DiffData {
    riskDirData: RiskDirectoryDto | undefined;
    riskEntitiesData: EntityOverviewListDto | undefined;
    riskNodesData: RiskDirectoryNodeDto | undefined;
}

interface MapPageState {
    drawer: MapViewDrawer | undefined;
    status: MapActionStatus;
    popupState: PopupState | undefined;
    pageState: RiskMapPageStateRaw;
    aiSummaryEnabled: boolean;
    insightsEditorDates: [Moment | null, Moment | null] | null;
}

interface FailedAssessmentPopup {
    type: "FailedAssessment";
    failingNodes: MissingRequiredColumnsDetails;
    formattedView: FormattedView<DV.NodeDescriptor<T.MapNode>>;
}

interface ExportRiskMatrixPopup {
    type: "ExportRiskMatrix";
}

type PopupState = FailedAssessmentPopup | ExportRiskMatrixPopup;

type MapActionStatus = { type: "DEFAULT" } | { type: "UPDATING" };

interface MapViewDrawer {
    nodeId: string;
}

interface MetadataColors {
    [optionLabel: string]: string;
}

interface SimpleAndFilterWithContext<T extends object> extends SimpleAndFilter {
    filters: Array<SimpleOrFilterWithContext<T>>;
}

interface SimpleOrFilterWithContext<T extends object> extends SimpleOrFilter {
    filters?: Array<SimpleFieldFilter & T>;
}

interface NodeWithMissingColumns {
    item: DV.NodeDescriptor<T.MapNode>;
    missing: string[];
}

const GREEN = "#9CCF91";
const YELLOW = "#EFE47B";
const RED = "#F47676";
const STOPS = [GREEN, YELLOW, RED];

function colorizeMetadata(metadataValues: Array<R.OptionValue>) {
    const metadataColors: MetadataColors = {};
    if (metadataValues.length === 0) {
        if (window.console) {
            console.warn("Empty metadata values");
        }
        return metadataColors;
    }
    const optionsCount = metadataValues.length;
    const stops = STOPS.map((color) => Color(color));
    let currentLowerBoundStopPos = 0;
    let currentUpperBoundStopPos = 1;
    let currentLowerBoundRatio = (currentUpperBoundStopPos - 1) / stops.length;
    let currentUpperBoundRatio = currentUpperBoundStopPos / stops.length;
    metadataValues.forEach((optionValue, index) => {
        const ratio = index / optionsCount;
        if (ratio > currentUpperBoundRatio) {
            if (currentUpperBoundStopPos === stops.length - 1) {
                metadataColors[optionValue.value] =
                    stops[currentUpperBoundStopPos].hex();
                return;
            }
            currentLowerBoundStopPos++;
            currentUpperBoundStopPos = Math.min(
                currentUpperBoundStopPos + 1,
                stops.length - 1
            );
            currentLowerBoundRatio = currentLowerBoundStopPos / stops.length;
            currentUpperBoundRatio = currentUpperBoundStopPos / stops.length;
        }
        const startColor = stops[currentLowerBoundStopPos];
        const endColor = stops[currentUpperBoundStopPos];
        // A value between 0 and 1, with 0 meaning the right color is
        // startColor and 1 endColor
        const ratioWithinBounds =
            (ratio - currentLowerBoundRatio) /
            (currentUpperBoundRatio - currentLowerBoundRatio);
        const deltaHue =
            (endColor.hue() - startColor.hue()) * ratioWithinBounds;
        const deltaSaturation =
            (endColor.saturationl() - startColor.saturationl()) *
            ratioWithinBounds;
        const deltaLightness =
            (endColor.lightness() - startColor.lightness()) * ratioWithinBounds;
        const deltaAlpha =
            (endColor.alpha() - startColor.alpha()) * ratioWithinBounds;
        let hue = startColor.hue() + deltaHue;
        let saturation = startColor.saturationl() + deltaSaturation;
        let lightness = startColor.lightness() + deltaLightness;
        let alpha = startColor.alpha() + deltaAlpha;
        if (hue > 360) {
            hue -= 360;
        } else if (hue < 0) {
            hue += 360;
        }
        if (saturation > 100) {
            saturation = 100;
        } else if (saturation < 0) {
            saturation = 0;
        }
        if (lightness > 100) {
            lightness = 100;
        } else if (lightness < 0) {
            lightness = 0;
        }
        if (alpha > 1) {
            alpha = 1;
        } else if (alpha < 0) {
            alpha = 0;
        }
        const color = Color()
            .hue(hue)
            .saturationl(saturation)
            .lightness(lightness)
            .alpha(alpha);

        metadataColors[optionValue.value] = color.hex();
    });
    return metadataColors;
}

function MetadataCircle(props: {
    colors: MetadataColors;
    label: string | undefined;
}) {
    const { colors, label } = props;
    if (!label) {
        // Insert a non-breakable space where there is no data,
        // otherwise the cells are way too small
        return <span>{"\xa0"}</span>;
    }
    const color = colors[label];
    return (
        <span>
            <span style={{ color }}>{"\u25cf"}</span> {label}
        </span>
    );
}

// Just like memoizeOne, but index the memoized results by key
function memoizeMap<Key extends string, Args extends unknown[], Ret>(
    resultFn: (this: any, key: Key, ...newArgs: Args) => Ret
): (this: any, key: Key, ...newArgs: Args) => Ret {
    const map: Partial<Record<string, typeof resultFn>> = {};

    function memoizedInMap(this: unknown, key: Key, ...newArgs: Args) {
        if (!Object.hasOwn(map, key)) {
            map[key] = memoizeOne(resultFn);
        }
        return map[key]!(key, ...newArgs);
    }

    return memoizedInMap;
}
